import { nanoid } from "nanoid";
import { db } from "./DBHelper";
import { eventsEmitter } from "utils/events";
import {
  COLLECTIONS,
  SALE_SYNC_STATUS,
  ROUTES,
  INVOICE_FORMATS,
  PAYMENT_MODES,
  STORAGE_KEYS,
  SALE_STATUS,
  CASHBOOK_ENTRY_TYPE,
  ORDER_TYPES,
  EVENTS,
  ADD_TO_CART_OPTIONS,
  CHARGE_CATEGORIES,
  SHIFT_SYNC_STATUS,
  CASHBOOK_DESCRIPTIONS,
  TAX_PREFERENCES,
  KOT_STATUS,
  APP_SETTING_KEYS,
  KEYPAD_BUTTONS,
  KEYBOARD_KEYS,
  STOCK_CHECK_OPTIONS,
  INVOICE_ACTIONS,
  INVOICE_TARGET_TYPE,
  PRINTER_TYPES,
  ONLINE_ORDER_STATUS,
  ONLINE_PAYMENT_STATUS,
} from "const";
import * as ShiftHelper from "./ShiftHelper";
import * as TerminalHelper from "./TermialHelper";
import * as SalespersonHelper from "./SalespersonHelper";
import SalesService from "services/SalesService";
import ShopService from "services/ShopService";
import CartHelper from "./CartHelper";
import * as NumberFormatHelper from "./NumberFormatHelper";
import { amountRoundOff, quantityRoundOff, saleAutoRoundOff } from "./NumberFormatHelper";
import toast from "react-hot-toast";
import DateTimeHelpers from "./DateTimeHelpers";
import * as ProductHelper from "./ProductHelper";
import CustomerHelper from "./CustomerHelper";
import * as TableAreaHelper from "./TableAreaHelper";
import * as EstimateHelper from "helpers/EstimateHelper";
import ConfigHelper from "helpers/ConfigHelper";
import TaxHelper from "helpers/TaxHelper";
import ShopsHelper from "helpers/ShopsHelper";
import KotManager from "./KotManager";
import * as InvoiceActionHelper from "./InvoiceActionHelper";
import * as InvoiceHelper from "./InvoiceHelper";
import AppBridgeHelper from "./AppBridgeHelper";
import { bytesToBase64 } from "helpers/StringHelper";
import { PrinterManager } from "managers/PrinterManager";
import WebSocketGatewayService from "services/WebSocketGatewayService";

async function processSaleWithBillNo(settingsStore, salesStore, saleData) {
  const prefixSetting = await settingsStore.get(APP_SETTING_KEYS.BILL_NUMBER_PREFIX);
  const suffixSetting = await settingsStore.get(APP_SETTING_KEYS.NEXT_BILL_NO);

  const prefix = prefixSetting?.value ?? "";
  let suffix = Number(suffixSetting?.value ?? 1);
  if (isNaN(suffix) || suffix < 1) suffix = 1;

  const billNo = `${prefix}${suffix}`;

  const saleToSave = {
    ...saleData,
    bill_no: billNo,
    bare_bill_no: suffix,
  };

  const saleId = await salesStore.put(saleToSave);
  return { ...saleToSave, local_id: saleId };
}

// save + assign bill_no
export const saveSaleToDb = async (saleData = {}, paymentData = null) => {
  const storeNames = [COLLECTIONS.APP_SETTINGS.name, COLLECTIONS.SALES.name];
  if (paymentData) storeNames.push(COLLECTIONS.SHIFT_TRANSACTIONS.name);

  return db.runTransaction(
    storeNames,
    async (stores) => {
      const settingsStore = stores[COLLECTIONS.APP_SETTINGS.name];
      const salesStore = stores[COLLECTIONS.SALES.name];
      let savedSale = saleData;

      if (!savedSale.bill_no) {
        savedSale = await processSaleWithBillNo(settingsStore, salesStore, savedSale);
        await settingsStore.put({ key: APP_SETTING_KEYS.NEXT_BILL_NO, value: savedSale.bare_bill_no + 1 });
      }

      return savedSale;
    },
    "creating sale with payment transaction",
  );
};

const fetchCustomer = async (customer) => {
  if (!customer || !customer.mobile) return customer;

  try {
    const existingCustomers = await db.filter(COLLECTIONS.CUSTOMERS.name, {
      mobile: customer.mobile,
    });

    if (existingCustomers && existingCustomers.length > 0) {
      return existingCustomers[0];
    }
  } catch (error) {
    console.error("Error resolving customer by mobile:", error);
  }
  return customer;
};

export const notifySalesUpdate = (sale) => {
  eventsEmitter.emit(EVENTS.SALES_UPDATED, sale);
  if (!sale?.editing_sale_local_id) {
    WebSocketGatewayService.broadcast({ action: "CARTS_UPDATED", payload: sale || null });
    WebSocketGatewayService.broadcast({ action: "TABLES_UPDATED" });
  }
};

export const getInitialSaleData = (saleData = {}) => {
  const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);

  return {
    items: [],
    payment_modes: [],
    payable: 0,
    discount_applied_on_items: 0,
    discount: 0,
    cess: 0,
    tax: 0,
    shift: {
      id: Number(shiftId),
    },
    cashier: SalespersonHelper.getCurrentSalesperson() || null,
    time: DateTimeHelpers.getLocalISOString(),
    status: SALE_STATUS.DRAFT,
    sync_status: SALE_SYNC_STATUS.PENDING,
    kot_status: KOT_STATUS.DRAFT,
    deletedItems: [],
    selectedChargeGroups: [],
    remark: "",
    ...saleData,
  };
};

export const getHighestBillNumberInLocal = async () => {
  try {
    const allSales = await db.getAll(COLLECTIONS.SALES.name);
    if (!allSales || allSales.length === 0) return 0;
    let highest = 0;
    for (const sale of allSales) {
      const num = Number(sale.bare_bill_no);
      if (!isNaN(num) && num > highest) {
        highest = num;
      }
    }
    return highest;
  } catch (err) {
    console.error("Error getting highest bill number in device:", err);
    return 0;
  }
};

function applyShopChargesToSummary(
  shopCharges,
  rawPayable,
  normal_payable,
  totalChargeAmount,
  extraDiscount,
  isDiscountSpread = false,
  total = 0,
  tax = 0,
  cess = 0,
) {
  let totalSaleChargeAmount = 0;
  let totalSaleDiscountAmount = 0;
  (shopCharges || []).forEach((shopCharge) => {
    if (shopCharge.category === CHARGE_CATEGORIES.DISCOUNT) {
      totalSaleDiscountAmount += Number(shopCharge.value || 0);
    } else {
      totalChargeAmount += Number(shopCharge.value || 0);
      totalSaleChargeAmount += Number(shopCharge.value || 0);
    }
  });

  totalSaleDiscountAmount += Number(extraDiscount || 0);

  let adjustedTotal = total;
  let adjustedTax = tax;
  let adjustedCess = cess;
  let adjustedRawPayable = rawPayable;
  let adjustedNormalPayable = normal_payable;

  if (isDiscountSpread) {
    adjustedRawPayable += totalSaleChargeAmount;
    adjustedNormalPayable += totalSaleChargeAmount;
  } else if (totalSaleDiscountAmount > 0) {
    const taxPreference = TaxHelper.getTaxPreference();
    const isIncludingTax = taxPreference === TAX_PREFERENCES.INCLUDING_TAX;

    if (isIncludingTax) {
      const grossBeforeDiscount = rawPayable;
      const discountRatio = grossBeforeDiscount > 0 ? Math.min(1, totalSaleDiscountAmount / grossBeforeDiscount) : 0;

      adjustedTax = amountRoundOff(tax * (1 - discountRatio));
      adjustedCess = amountRoundOff(cess * (1 - discountRatio));
      adjustedTotal = amountRoundOff(total * (1 - discountRatio));

      const discountedPayable = Math.max(0, grossBeforeDiscount - totalSaleDiscountAmount);
      adjustedRawPayable = discountedPayable + totalSaleChargeAmount;
      adjustedNormalPayable = Math.max(0, normal_payable - totalSaleDiscountAmount) + totalSaleChargeAmount;
    } else {
      const baseBeforeDiscount = total;
      const discountRatio = baseBeforeDiscount > 0 ? Math.min(1, totalSaleDiscountAmount / baseBeforeDiscount) : 0;

      adjustedTax = amountRoundOff(tax * (1 - discountRatio));
      adjustedCess = amountRoundOff(cess * (1 - discountRatio));
      const discountedBase = Math.max(0, baseBeforeDiscount - totalSaleDiscountAmount);
      adjustedTotal = amountRoundOff(discountedBase);

      adjustedRawPayable = discountedBase + adjustedTax + adjustedCess + totalSaleChargeAmount;
      adjustedNormalPayable = adjustedRawPayable;
    }
  } else {
    adjustedRawPayable += totalSaleChargeAmount;
    adjustedNormalPayable += totalSaleChargeAmount;
  }

  return {
    rawPayable: adjustedRawPayable,
    normal_payable: adjustedNormalPayable,
    totalChargeAmount,
    totalSaleDiscountAmount,
    totalSaleChargeAmount,
    total: adjustedTotal,
    tax: adjustedTax,
    cess: adjustedCess,
  };
}

export const getSaleChargeDiscountTotal = (saleCharges = []) => {
  return (saleCharges || [])
    .filter((c) => c.category === CHARGE_CATEGORIES.DISCOUNT)
    .reduce((sum, c) => sum + Number(c.value || 0), 0);
};

function calculateCartBaseTotals(items, isNC) {
  if (isNC) {
    return {
      total: 0,
      rawPayable: 0,
      cess: 0,
      tax: 0,
      discount: 0,
      normal_payable: 0,
      totalChargeAmount: 0,
    };
  }
  let totalChargeAmount = 0;
  (items || []).forEach((item) => {
    totalChargeAmount += item.totalCharges || 0;
  });
  return {
    total: CartHelper.getTotalAmount(items),
    rawPayable: CartHelper.getTotalPayable(items),
    cess: CartHelper.getTotalCessAmount(items),
    tax: CartHelper.getTotalTaxAmount(items),
    discount: CartHelper.getTotalDiscount(items),
    normal_payable: CartHelper.getTotalNormalPayable(items),
    totalChargeAmount,
  };
}

export const calculateSaleSummary = (
  items,
  {
    charges = [],
    saleLevelChargeGroupIds = null,
    existingSaleCharges = null,
    extraDiscount = 0,
    orderType = null,
    isDiscountSpread = false,
  } = {},
) => {
  const isNC = orderType === ORDER_TYPES.NC;
  let { total, rawPayable, cess, tax, discount, normal_payable, totalChargeAmount } = calculateCartBaseTotals(
    items,
    isNC,
  );

  const shopCharges = isNC
    ? []
    : charges && charges.length > 0
      ? TaxHelper.getSaleCharges(total, tax, charges, saleLevelChargeGroupIds)
      : existingSaleCharges || [];

  let totalSaleChargeAmount = 0;
  let totalSaleDiscountAmount = 0;

  if (!isNC) {
    const res = applyShopChargesToSummary(
      shopCharges,
      rawPayable,
      normal_payable,
      totalChargeAmount,
      extraDiscount,
      isDiscountSpread,
      total,
      tax,
      cess,
    );

    rawPayable = res.rawPayable;
    normal_payable = res.normal_payable;
    totalChargeAmount = res.totalChargeAmount;
    totalSaleDiscountAmount = res.totalSaleDiscountAmount;
    totalSaleChargeAmount = res.totalSaleChargeAmount;
    total = res.total;
    tax = res.tax;
    cess = res.cess;
  }

  const roundedPayable = isNC ? 0 : saleAutoRoundOff(rawPayable);
  const roundOff = isNC ? 0 : amountRoundOff(roundedPayable - rawPayable);

  return {
    total: amountRoundOff(total),
    payable: amountRoundOff(roundedPayable),
    cess: amountRoundOff(cess),
    tax: amountRoundOff(tax),
    discount_applied_on_items: amountRoundOff(discount),
    discount: amountRoundOff(totalSaleDiscountAmount),
    extraDiscount: amountRoundOff(extraDiscount || 0),
    roundOff,
    normal_payable: amountRoundOff(normal_payable),
    totalChargeAmount,
    totalSaleChargeAmount,
    charges: shopCharges,
    saleLevelChargeGroupIds,
  };
};

export const calculateDueAndChange = (sale, payable) => {
  const isNC = sale?.order_type === ORDER_TYPES.NC;

  if (isNC) {
    return {
      due: 0,
      change: 0,
      prepaid: 0,
    };
  }

  const prepaidAmount = Number(sale?.prepaid || 0);

  const due = amountRoundOff(Math.max(payable - prepaidAmount, 0));
  const change = amountRoundOff(Math.max(prepaidAmount - payable, 0));
  const prepaid = amountRoundOff(payable - due);

  return {
    due,
    change,
    prepaid,
  };
};

function resolveSaleItemPriceCategoryRate(saleItem, priceCategory, priceCategoryItems, rate) {
  if (priceCategory && priceCategoryItems && priceCategoryItems.length > 0 && saleItem.manualRate == null) {
    const categoryPrice = CartHelper.getApplicableCategoryPrice(
      saleItem.productId,
      saleItem.qty,
      priceCategory,
      priceCategoryItems,
    );
    if (categoryPrice !== null && categoryPrice !== undefined) {
      saleItem.priceCategoryRate = categoryPrice;
    } else {
      delete saleItem.priceCategoryRate;
    }
  } else if (rate != null && saleItem.manualRate == null && priceCategory) {
    saleItem.priceCategoryRate = rate;
  }
}

export const calculateSaleItemSummary = (
  saleItem,
  {
    orderType,
    charges = [],
    selectedChargeGroupIds = null,
    priceCategory = null,
    priceCategoryItems = null,
    rate = null,
  } = {},
) => {
  const nc = orderType === ORDER_TYPES.NC;

  const taxPreference = TaxHelper.getTaxPreference();
  const isExcludingTax = taxPreference === TAX_PREFERENCES.EXCLUDING_TAX;

  resolveSaleItemPriceCategoryRate(saleItem, priceCategory, priceCategoryItems, rate);

  const inputRate = TaxHelper.getSaleItemBaseRate(saleItem);

  const calculatedRate = TaxHelper.getItemUnitRate(
    inputRate,
    saleItem.taxPercentage,
    saleItem.cessPercentage,
    saleItem.discountPercentage,
  );

  let discountBaseRate = inputRate;
  if (isExcludingTax) {
    discountBaseRate =
      inputRate * (1 + (Number(saleItem.taxPercentage || 0) + Number(saleItem.cessPercentage || 0)) / 100);
  }
  const discount = nc
    ? 0
    : CartHelper.getItemDiscountAmount(discountBaseRate, saleItem.qty, saleItem.discountPercentage);
  const roundedQty = quantityRoundOff(saleItem.qty);
  // totalBeforeTax represents the actual taxable unit rate * quantity before taxes
  const totalBeforeTax = nc ? 0 : amountRoundOff(calculatedRate * roundedQty);
  const tax = TaxHelper.getItemTax(saleItem.taxPercentage, calculatedRate) * roundedQty;
  const cess = TaxHelper.getItemCess(saleItem.cessPercentage, calculatedRate) * roundedQty;

  const appliedCharges = nc ? [] : TaxHelper.getItemCharges(saleItem, charges, selectedChargeGroupIds);

  let itemTotalCharges = 0;
  let itemTotalDiscounts = 0;

  appliedCharges.forEach((appliedCharge) => {
    if (appliedCharge.category === CHARGE_CATEGORIES.DISCOUNT) {
      itemTotalDiscounts += appliedCharge.value;
    } else {
      itemTotalCharges += appliedCharge.value;
    }
  });

  const payable = calculatedRate * roundedQty + tax + cess + itemTotalCharges - itemTotalDiscounts;

  // rate: tax-exclusive discounted unit price
  saleItem.rate = nc ? 0 : amountRoundOff(calculatedRate);
  // rateAndTax: undiscounted unit price including tax (for EXCLUDING_TAX: inputRate * (1 + tax%), for INCLUDING_TAX: inputRate)
  saleItem.rateAndTax = isExcludingTax
    ? amountRoundOff(
        inputRate * (1 + (Number(saleItem.taxPercentage || 0) + Number(saleItem.cessPercentage || 0)) / 100),
      )
    : amountRoundOff(inputRate);

  saleItem.totalBeforeTax = totalBeforeTax;
  saleItem.normal_payable = nc ? 0 : amountRoundOff(payable);
  saleItem.discount = amountRoundOff(discount + itemTotalDiscounts);
  saleItem.tax = nc ? 0 : tax;
  saleItem.cess = nc ? 0 : cess;
  saleItem.charges = appliedCharges;
  saleItem.totalCharges = itemTotalCharges;
  saleItem.totalDiscounts = itemTotalDiscounts;
  saleItem.payable = nc ? 0 : payable;

  return saleItem;
};

export const getActiveSalePriceCategory = (sale, selectedPriceCategory = undefined) => {
  if (sale?.customer?.group?.price_category) {
    return sale.customer.group.price_category;
  }
  if (selectedPriceCategory !== undefined) {
    return selectedPriceCategory;
  }
  if (sale?.price_category !== undefined) {
    return sale.price_category;
  }
  return sale?.table?.area?.price_category || null;
};

export const resolveTableAreaDetails = async (tableId, chairId = null) => {
  if (!tableId) return null;

  let tableInfo = await TableAreaHelper.getTableById(tableId);
  const matchedChair = chairId != null ? (tableInfo?.chairs || []).find((c) => Number(c.id) === Number(chairId)) : null;

  return {
    id: tableId,
    name: tableInfo?.name,
    area: {
      id: tableInfo?.area?.id,
      name: tableInfo?.area?.name,
      price_category: tableInfo?.area?.price_category,
    },
    chair: matchedChair ? { id: matchedChair.id, name: matchedChair.name } : null,
  };
};

const resolveEffectiveItemRate = (product, quantity, priceCategory, priceCategoryItems, saleItem) => {
  if (priceCategory) {
    const rate = getSaleItemRate(product, quantity, priceCategory, priceCategoryItems || [], saleItem?.option);
    if (rate !== undefined && rate !== null) {
      return rate;
    }
  }
  return saleItem.rate;
};

export const recalculateSale = (sale, updatedItems, options = {}) => {
  const tempSale = { ...sale, items: updatedItems };
  const { selectedChargeGroupIds, saleLevelChargeGroupIds } = getActiveChargeGroupIds(tempSale);

  const totals = calculateSaleSummary(updatedItems, {
    charges: options.charges || [],
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    existingSaleCharges: sale?.charges,
    extraDiscount: sale?.extraDiscount || 0,
    orderType: sale?.order_type,
  });

  const dueAndChange = calculateDueAndChange(sale, totals.payable);

  return {
    ...sale,
    items: updatedItems,
    ...totals,
    ...dueAndChange,
    selectedChargeGroupIds,
    saleLevelChargeGroupIds,
  };
};

export const cleanPaymentModes = (sale) => {
  let modes = (sale?.payment_modes || []).map(({ _isNewPayment, ...rest }) => rest);
  if (Number(sale?.change || 0) <= 0) {
    modes = modes.filter((pm) => Number(pm.amount || 0) >= 0);
  }
  return modes;
};

const calculateSaleItems = async (saleData, selectedChargeGroupIds, charges) => {
  const priceCategoryItems = saleData?.price_category ? await ProductHelper.getPriceCategoryItemsFromDB() : [];
  return Promise.all(
    (saleData?.items || []).map(async (saleItem) => {
      const product = await ProductHelper.getProductById(saleItem.id);
      if (!product) return { ...saleItem, localId: saleItem.localId || nanoid() };

      const quantity = amountRoundOff(saleItem.qty || saleItem.quantity || 1);
      const itemRate = resolveEffectiveItemRate(
        product,
        quantity,
        saleData.price_category,
        priceCategoryItems,
        saleItem,
      );

      const data = CartHelper.getCartItemData(
        product,
        saleItem,
        quantity,
        {
          orderType: saleData?.order_type,
          selectedChargeGroupIds,
          rate: itemRate,
        },
        charges,
      );
      return {
        ...data,
        localId: saleItem.localId || nanoid(),
      };
    }),
  );
};

export const createNewSale = async (saleData, options = {}) => {
  const currentShiftId = ShiftHelper.getCurrentShiftId();

  const { selectedChargeGroupIds, saleLevelChargeGroupIds } = getActiveChargeGroupIds(saleData);

  if (saleData?.table) {
    const chairId = saleData.table.chairId ?? saleData.table.chair?.id ?? null;
    const table = await resolveTableAreaDetails(saleData.table.id, chairId);
    if (table) {
      saleData.table = table;
      if (table?.area?.price_category && !saleData.price_category) {
        saleData.price_category = table.area.price_category;
      }
    }
  }

  let finalCustomer = null;
  if (saleData?.customer) {
    const fetchedCustomer = await fetchCustomer(saleData.customer);
    finalCustomer = fetchedCustomer ? { ...fetchedCustomer, ...saleData.customer } : saleData.customer;
    if (!saleData.price_category && finalCustomer?.group?.price_category) {
      saleData.price_category = finalCustomer.group.price_category;
    }
  }

  const calculatedItems = await calculateSaleItems(saleData, selectedChargeGroupIds, options.charges);

  const totals = calculateSaleSummary(calculatedItems, {
    ...options,
    saleLevelChargeGroupIds,
    orderType: saleData?.order_type,
  });

  const dueAndChange = calculateDueAndChange(saleData, totals.payable);

  const newSale = {
    ...saleData,
    selectedChargeGroups: saleData?.selectedChargeGroups || options.selectedChargeGroups || [],
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    items: calculatedItems,
    customer: finalCustomer || null,
    payment_modes: saleData.payment_modes || [],
    ...totals,
    ...dueAndChange,
    roundOff: totals?.roundOff || 0,
    shift: currentShiftId,
    cashier: saleData.cashier || null,
    time: DateTimeHelpers.getLocalISOString(),
    status: SALE_STATUS.DRAFT,
    sync_status: SALE_SYNC_STATUS.PENDING,
    price_category: saleData.price_category || null,
  };

  const local_id = await createNewSaleInDB(newSale, options);
  newSale.local_id = local_id;

  return newSale;
};

export const createNewSaleInDB = async (data, options = {}) => {
  if (!data.cashier) {
    data.cashier = SalespersonHelper.getCurrentSalesperson() || null;
  }

  data.payment_modes = cleanPaymentModes(data);
  const local_id = await db.add(COLLECTIONS.SALES.name, data);
  data.local_id = local_id;

  if (!options.skipShiftPaymentsSave && !data.editing_sale_local_id && data.status !== SALE_STATUS.DRAFT) {
    await saveSaleShiftPayments(data);
  }

  if (!options.skipNotify && (options.notify || options.notify === undefined)) {
    notifySalesUpdate(data);
  }
  return local_id;
};

async function logInitialSalePayments(toUpdate, shiftId, now) {
  for (const pm of toUpdate.payment_modes) {
    const modeId = Number(pm.id || pm.mode_id);
    const amt = Number(pm.amount) || 0;
    if (amt === 0) continue;

    const isCredit = amt > 0;
    await ShiftHelper.addShiftPayment({
      shift_id: Number(shiftId),
      local_id: modeId,
      name: pm.name,
      amount: isCredit ? amt : 0,
      debit: !isCredit ? Math.abs(amt) : 0,
      description: pm.description || (!isCredit ? CASHBOOK_DESCRIPTIONS.BALANCE : ""),
      sale_local_id: toUpdate.local_id,
      sale_bill_no: toUpdate.bill_no,
      time: now,
      type: CASHBOOK_ENTRY_TYPE.SALE,
    });
  }
}

async function saveSaleShiftPayments(toUpdate) {
  if (toUpdate.status === SALE_STATUS.DRAFT || !Array.isArray(toUpdate.payment_modes)) {
    return;
  }

  const shiftId = toUpdate.shift?.id;
  if (!shiftId) return;

  const existingPaymentTransactions = await ShiftHelper.filterShiftPayments({ sale_local_id: toUpdate.local_id });

  // If no prior shift payments exist for this sale, this is an initial finalization.
  // Log each payment mode entry directly (positive = tender received, negative = change returned).
  if (!existingPaymentTransactions || existingPaymentTransactions.length === 0) {
    return await logInitialSalePayments(toUpdate, shiftId, DateTimeHelpers.getLocalISOString());
  }

  const alreadyLoggedByMode = {};
  (existingPaymentTransactions || []).forEach((txn) => {
    const modeId = Number(txn.local_id);
    const netAmt = (Number(txn.amount) || 0) - (Number(txn.debit) || 0);
    alreadyLoggedByMode[modeId] = (alreadyLoggedByMode[modeId] || 0) + netAmt;
  });

  const targetByMode = {};
  const modeInfoMap = {};
  (toUpdate.payment_modes || []).forEach((pm) => {
    const modeId = Number(pm.id || pm.mode_id);
    const amt = Number(pm.amount) || 0;
    targetByMode[modeId] = (targetByMode[modeId] || 0) + amt;
    if (!modeInfoMap[modeId]) {
      modeInfoMap[modeId] = pm;
    }
  });

  const allModeIds = new Set([
    ...Object.keys(alreadyLoggedByMode).map(Number),
    ...Object.keys(targetByMode).map(Number),
  ]);

  // Tracks net payment changes per mode (targetAmt - alreadyLoggedAmt).
  // Positive = new payment received (credit), Negative = refund/mode change (debit).
  const paymentDifferences = [];
  for (const modeId of allModeIds) {
    const targetAmt = targetByMode[modeId] || 0;
    const loggedAmt = alreadyLoggedByMode[modeId] || 0;
    const diff = amountRoundOff(targetAmt - loggedAmt);
    if (diff !== 0) {
      const pm =
        modeInfoMap[modeId] || (existingPaymentTransactions || []).find((t) => Number(t.local_id) === modeId) || {};
      paymentDifferences.push({
        modeId,
        name: pm.name,
        description: pm.description,
        diff,
      });
    }
  }

  if (paymentDifferences.length === 0) return;

  const debits = paymentDifferences.filter((d) => d.diff < 0);
  const credits = paymentDifferences.filter((d) => d.diff > 0);
  const isSwap = debits.length > 0 && credits.length > 0;

  const debitNames = debits.map((d) => d.name).join(", ");
  const creditNames = credits.map((c) => c.name).join(", ");

  for (const item of paymentDifferences) {
    const isCredit = item.diff > 0;
    const type = isSwap ? CASHBOOK_ENTRY_TYPE.PAYMENT_MODE_CHANGE : CASHBOOK_ENTRY_TYPE.SALE;

    let description = item.description || "";
    if (isSwap) {
      description = isCredit
        ? `${CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_FROM} ${debitNames}`
        : `${CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_TO} ${creditNames}`;
    }

    await ShiftHelper.addShiftPayment({
      shift_id: toUpdate.shift?.id,
      local_id: item.modeId,
      name: item.name,
      amount: isCredit ? item.diff : 0,
      debit: !isCredit ? Math.abs(item.diff) : 0,
      description,
      sale_local_id: toUpdate.local_id,
      sale_bill_no: toUpdate.bill_no,
      time: DateTimeHelpers.getLocalISOString(),
      type,
    });
  }
}

export const saveSaleInDB = async (sale, options = {}) => {
  if (!sale.local_id) {
    return await createNewSaleInDB(sale, options);
  }

  const currentShiftId = ShiftHelper.getCurrentShiftId();

  const existing = await getSaleById(sale.local_id);

  const toUpdate = {
    ...(existing || {}),
    ...sale,
    shift: sale?.shift?.id ? sale.shift : existing?.shift || currentShiftId,
  };

  if (existing?.id && !sale.id) toUpdate.id = existing.id;

  if (
    existing?.sync_status &&
    (!sale.sync_status || sale.sync_status === SALE_SYNC_STATUS.PENDING) &&
    existing.sync_status === SALE_SYNC_STATUS.SYNCED_TO_CLOUD
  ) {
    toUpdate.sync_status = existing.sync_status;
    toUpdate.sync_message = existing.sync_message;
  }

  toUpdate.payment_modes = cleanPaymentModes(toUpdate);
  await db.update(COLLECTIONS.SALES.name, toUpdate.local_id, toUpdate);

  if (!options.skipShiftPaymentsSave && !toUpdate.editing_sale_local_id && toUpdate.status !== SALE_STATUS.DRAFT) {
    await saveSaleShiftPayments(toUpdate);
  }
  if (!options.skipNotify && (options.notify || options.notify === undefined)) {
    notifySalesUpdate(toUpdate);
  }
  return toUpdate.local_id;
};

export const isSaleActive = (sale, terminalSettings = TerminalHelper.getTerminalSettings()) => {
  if (!sale) return false;
  const allowedStatuses = [SALE_STATUS.DRAFT, SALE_STATUS.PRINTED];
  if (terminalSettings?.displayAwaitingDispatchOrders) {
    allowedStatuses.push(SALE_STATUS.PAID);
  }
  if (!allowedStatuses.includes(sale.status)) return false;
  if (sale.estimate_no && !sale.bill_no) return false;
  if (sale.status === SALE_STATUS.PAID) {
    const isAwaitingDispatch = [KOT_STATUS.PENDING, KOT_STATUS.PREPARING, KOT_STATUS.READY].includes(sale.kot_status);
    if (!isAwaitingDispatch) return false;
  }
  return true;
};

export const getActiveSales = async (salesPersonId) => {
  const terminalSettings = TerminalHelper.getTerminalSettings();
  const query = { status: [SALE_STATUS.DRAFT, SALE_STATUS.PRINTED] };

  if (terminalSettings?.displayAwaitingDispatchOrders) {
    query.status.push(SALE_STATUS.PAID);
  }

  if (salesPersonId) query.sales_person_id = salesPersonId;

  const activeSales = await db.filter(COLLECTIONS.SALES.name, query);

  return activeSales.filter((sale) => isSaleActive(sale, terminalSettings));
};

export const getActiveCartsSummary = async (shiftId) => {
  const query = {
    status: [SALE_STATUS.DRAFT, SALE_STATUS.PRINTED],
    "shift.id": Number(shiftId),
  };

  const activeSales = await db.filter(COLLECTIONS.SALES.name, query);

  const activeCarts = (activeSales || []).filter((sale) => !sale.bill_no);

  return {
    activeCartsCount: activeCarts.length,
    activeCartsValue: activeCarts.reduce((sum, sale) => sum + (Number(sale.payable) || 0), 0),
    activeCarts,
  };
};

export const getSales = () => {
  return db.getAll(COLLECTIONS.SALES.name);
};

export const getSaleByEditingSaleLocalId = async (localId) => {
  if (localId === null || localId === undefined) return null;

  const sales = await db.filter(COLLECTIONS.SALES.name, { editing_sale_local_id: localId });

  return sales?.[0] || null;
};

export const getSaleById = async (id) => {
  if (id === null || id === undefined) return null;

  const sale = await db.get(COLLECTIONS.SALES.name, id);
  return sale;
};

export const getSaleByInvoiceNumberSuffix = async (suffix) => {
  const sales = await db.filter(COLLECTIONS.SALES.name, { bare_bill_no: Number(suffix) });
  return sales.length > 0 ? sales[0] : null;
};

export const getSaleByOnlineOrderId = async (onlineOrderId) => {
  if (onlineOrderId === null || onlineOrderId === undefined) return null;
  const sales = await db.filter(COLLECTIONS.SALES.name, (s) => String(s.online_order_id) === String(onlineOrderId));
  return sales && sales.length > 0 ? sales[0] : null;
};

export const deleteSale = async (id, options = {}) => {
  if (id === null || id === undefined) return;

  await db.delete(COLLECTIONS.SALES.name, id);
  if (!options.skipNotify && (options.notify || options.notify === undefined)) {
    notifySalesUpdate();
  }
};

export const getSalesByShift = async (shiftId) => {
  const targetShiftId = Number(shiftId);

  return await db.filter(COLLECTIONS.SALES.name, {
    "shift.id": targetShiftId,
    status: [SALE_STATUS.PAID, SALE_STATUS.RETURNED, SALE_STATUS.BUY_BACK],
  });
};

export const getEstimatesByShift = async (shiftId) => {
  const targetShiftId = Number(shiftId);

  const sales = await db.filter(COLLECTIONS.SALES.name, {
    "shift.id": targetShiftId,
  });

  return (sales || []).filter((sale) => !!sale.estimate_no);
};

async function sendSalePayloadToCloud(saleToSync) {
  if (saleToSync.id) {
    // Editing an existing synced sale
    const cleanPaymentModes = (saleToSync.payment_modes || [])
      .filter((pm) => pm.name !== PAYMENT_MODES.CREDIT && pm.name !== PAYMENT_MODES.POINTS)
      .map(({ _isNewPayment, ...rest }) => rest);

    const payload = {
      ...saleToSync,
      payment_modes: cleanPaymentModes,
      remark: saleToSync.remark || `Sale update at ${DateTimeHelpers.formatDateTime(new Date())}`,
    };

    const updatedData = await SalesService.updateSale(saleToSync.id, payload);
    return { status: 200, data: { sale_id: updatedData?.id || saleToSync.id } };
  } else {
    // Creating a new sale in cloud
    const payload = {
      ...saleToSync,
      payment_modes: (saleToSync.payment_modes || []).filter(
        (pm) => pm.name !== PAYMENT_MODES.CREDIT && pm.name !== PAYMENT_MODES.POINTS,
      ),
    };
    return await SalesService.createSaleV2(payload);
  }
}

function processCloudSyncResponse(responseData, saleToSync) {
  if (responseData?.status !== 200) {
    return {
      finalSyncStatus: SALE_SYNC_STATUS.FAILED,
      syncMessage: `Server Error: ${responseData?.message}`,
      saleId: "",
    };
  }
  const saleId = responseData?.data?.sale_id;
  if (!saleId) {
    return {
      finalSyncStatus: SALE_SYNC_STATUS.FAILED,
      syncMessage: "Failed to sync sale to cloud. No sale ID received from server.",
      saleId: "",
    };
  }
  if (saleToSync.customer?.id && Number(saleToSync.customer?.id) > 0) {
    CustomerHelper.fetchAndUpdateCustomerFromBE(saleToSync.customer.id);
  }
  return { finalSyncStatus: SALE_SYNC_STATUS.SYNCED_TO_CLOUD, syncMessage: "", saleId };
}

export const syncSaleToServer = async (saleToSync) => {
  if (saleToSync.estimate_no && !saleToSync.bill_no) {
    return EstimateHelper.syncEstimateOnlyToServer(saleToSync);
  }

  const isCloudSyncEnabled = localStorage.getItem(STORAGE_KEYS.CLOUD_SYNC_ENABLED) !== "false";
  if (!isCloudSyncEnabled) {
    await saveSaleInDB(
      { ...saleToSync, sync_status: SALE_SYNC_STATUS.SYNCED_TO_LOCAL, sync_message: "", id: saleToSync.id },
      { skipNotify: true, skipShiftPaymentsSave: true },
    );
    return SALE_SYNC_STATUS.SYNCED_TO_LOCAL;
  }

  if (Number(saleToSync?.due || 0) !== 0) {
    await saveSaleInDB(
      { ...saleToSync, sync_status: SALE_SYNC_STATUS.PENDING, sync_message: "", id: saleToSync.id },
      { skipNotify: true, skipShiftPaymentsSave: true },
    );
    return SALE_SYNC_STATUS.PENDING;
  }

  let finalSyncStatus = SALE_SYNC_STATUS.PENDING;
  let syncMessage = "";
  let saleId = "";

  try {
    const responseData = await sendSalePayloadToCloud(saleToSync);
    const syncResult = processCloudSyncResponse(responseData, saleToSync);
    finalSyncStatus = syncResult.finalSyncStatus;
    syncMessage = syncResult.syncMessage;
    saleId = syncResult.saleId;
  } catch (err) {
    const serverMessage = err?.response?.data?.message || err?.message || null;
    finalSyncStatus = serverMessage ? SALE_SYNC_STATUS.FAILED : SALE_SYNC_STATUS.PENDING;
    syncMessage = serverMessage ? `Server Error: ${serverMessage}` : "Network/Connection Failure. Retrying later.";
    console.error("syncSaleToServer error:", err);
  }

  try {
    await saveSaleInDB(
      { ...saleToSync, sync_status: finalSyncStatus, sync_message: syncMessage, id: saleId },
      { skipNotify: true, skipShiftPaymentsSave: true },
    );
  } catch (error) {
    console.error("Failed to update sale DB");
  }
  return finalSyncStatus;
};

export async function forceSyncPendingSales() {
  try {
    const failedSales = await db.filter(COLLECTIONS.SALES.name, {
      sync_status: SALE_SYNC_STATUS.FAILED,
    });

    if (failedSales && failedSales.length > 0) {
      for (const sale of failedSales) {
        await db.update(COLLECTIONS.SALES.name, sale.local_id, {
          ...sale,
          sync_status: SALE_SYNC_STATUS.PENDING,
          sync_message: "",
        });
      }
    }
  } catch (error) {
    console.error("Failed to update failed sales to pending:", error);
    toast.error("Failed to update failed sales to pending");
    return;
  }

  return syncPendingSales();
}

export async function syncPendingSales() {
  try {
    await CustomerHelper.syncPendingCustomers();
    await CustomerHelper.syncPendingCustomerLogs();
    await ShiftHelper.syncPendingShifts();

    const pendingSales = await db.filter(COLLECTIONS.SALES.name, {
      sync_status: [SALE_SYNC_STATUS.PENDING],
      status: [SALE_STATUS.PAID],
    });

    if (pendingSales.length > 0) {
      for (const sale of pendingSales) {
        await syncSaleToServer(sale);
      }
    }

    await ShiftHelper.syncPendingClosedShifts();
  } catch (err) {
    console.error("Failed to sync pending sales");
  }
}

export async function clearSyncedSales() {
  try {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const shiftsToKeep = terminalSettings?.numberOfShiftsToBeStoredOnDevice;

    let shiftIdsToKeep = new Set();

    if (shiftsToKeep !== undefined && shiftsToKeep !== null && shiftsToKeep > 0) {
      const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
      const sortedShifts = [...allShifts].sort((a, b) => Number(b.id) - Number(a.id));

      const shiftsToKeepList = sortedShifts.slice(0, shiftsToKeep);
      shiftsToKeepList.forEach((s) => shiftIdsToKeep.add(Number(s.id)));

      const newAllShifts = allShifts.filter((s) => {
        if (shiftIdsToKeep.has(Number(s.id))) return true;
        if (s.sync_status !== SHIFT_SYNC_STATUS.SYNCED) return true;
        return false;
      });
      localStorage.setItem(STORAGE_KEYS.ALL_SHIFTS, JSON.stringify(newAllShifts));
    }

    const syncedSales = await db.filter(COLLECTIONS.SALES.name, {
      sync_status: SALE_SYNC_STATUS.SYNCED_TO_CLOUD,
    });

    if (syncedSales.length === 0) return;

    for (const sale of syncedSales) {
      if (shiftsToKeep !== undefined && shiftsToKeep !== null && shiftsToKeep > 0) {
        if (sale.shift && sale.shift.id && shiftIdsToKeep.has(Number(sale.shift.id))) {
          continue;
        }
      }
      await db.delete(COLLECTIONS.SALES.name, sale.local_id);
      await ShiftHelper.deleteShiftPaymentsForSale(sale.local_id);
    }
  } catch (err) {
    console.error("Failed to clear synced sales:", err);
  }
}

export async function syncSaleAndPending(saleToSync) {
  if (saleToSync) {
    let status;

    if (saleToSync.estimate_no && saleToSync.id) {
      status = await syncEstimateToSale(saleToSync);
    } else {
      status = await syncSaleToServer(saleToSync);
    }

    if (status === SALE_SYNC_STATUS.SYNCED_TO_CLOUD) {
      syncPendingSales();
    }
    return status;
  }
}

export const syncEstimateToSale = async (sale) => {
  let finalSyncStatus = SALE_SYNC_STATUS.PENDING;
  let syncMessage = "";
  let saleId = sale.id;

  const isCloudSyncEnabled = localStorage.getItem(STORAGE_KEYS.CLOUD_SYNC_ENABLED) !== "false";

  if (!isCloudSyncEnabled) {
    finalSyncStatus = SALE_SYNC_STATUS.SYNCED_TO_LOCAL;
    syncMessage = "";
  } else {
    try {
      const payload = {
        ...sale,
        payment_modes: (sale.payment_modes || []).filter(
          (pm) => pm.name !== PAYMENT_MODES.CREDIT && pm.name !== PAYMENT_MODES.POINTS,
        ),
      };
      const res = await SalesService.createSaleV2(payload);
      if (res?.status === 200) {
        finalSyncStatus = SALE_SYNC_STATUS.SYNCED_TO_CLOUD;
        syncMessage = "";
        if (res?.data?.sale_id) {
          saleId = res.data.sale_id;
        }
      } else {
        finalSyncStatus = SALE_SYNC_STATUS.FAILED;
        syncMessage = `Server Error: ${res?.data?.message || res?.message}`;
      }
    } catch (error) {
      finalSyncStatus = SALE_SYNC_STATUS.FAILED;
      syncMessage = `Server Error: ${error?.response?.data?.message || error?.message}`;
      console.error("Estimate to sale sync failed:", error);
    }
  }

  const localUpdate = {
    ...sale,
    id: saleId,
    sync_status: finalSyncStatus,
    sync_message: syncMessage,
  };

  try {
    await saveSaleInDB(localUpdate, { skipNotify: true, skipShiftPaymentsSave: true });
  } catch (error) {
    console.error("Failed to update estimate sale DB", error);
  }

  return finalSyncStatus;
};

async function prepareUpdatedSaleItems(sale, finalOrderType, resolvedCharges) {
  let finalItems = sale.items || [];
  if (finalOrderType === ORDER_TYPES.NC) {
    return await Promise.all(
      finalItems.map(async (si) => {
        const product = (await ProductHelper.getProductById(si.id)) || si.item;
        if (!product) return si;
        return CartHelper.getCartItemData(product, si, si.qty, { orderType: finalOrderType }, resolvedCharges);
      }),
    );
  } else if (sale?.order_type === ORDER_TYPES.NC && finalOrderType !== ORDER_TYPES.NC) {
    return await Promise.all(
      finalItems.map(async (si) => {
        const product = (await ProductHelper.getProductById(si.id)) || si.item;
        if (!product) return si;
        return CartHelper.getCartItemData(
          product,
          si,
          si.qty,
          {
            orderType: finalOrderType,
            rate: product.price,
            price: product.price,
          },
          resolvedCharges,
        );
      }),
    );
  }
  return finalItems;
}

function createPaymentSummary(paymentModes, discount, tenderData, balanceEntries) {
  const calculatedTender = paymentModes
    .filter(
      (m) => m.name !== PAYMENT_MODES.DISCOUNT && m.name !== PAYMENT_MODES.DISCOUNT_PERCENTAGE && Number(m.amount) > 0,
    )
    .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);

  const calculatePaymentSummary = (targetPayable) => {
    const calculatedDue = amountRoundOff(
      Math.max(targetPayable - (calculatedTender + (discount > 0 ? 0 : Number(discount || 0))), 0),
    );
    const calculatedChange = amountRoundOff(Math.max(calculatedTender - targetPayable, 0));
    const calculatedPrepaid = amountRoundOff(targetPayable - calculatedDue);

    let finalPaymentModes = [...tenderData];

    if (calculatedChange > 0) {
      const totalBalanceEntries = (balanceEntries || []).reduce((sum, b) => sum + Math.abs(Number(b.amount) || 0), 0);
      if (balanceEntries && balanceEntries.length > 0 && amountRoundOff(totalBalanceEntries) === calculatedChange) {
        finalPaymentModes = [...tenderData, ...balanceEntries];
      } else if (tenderData.length > 0) {
        const cashTender = tenderData.find((t) => (t.name || "").toLowerCase() === PAYMENT_MODES.CASH);
        const changeMode = cashTender || tenderData[0];
        finalPaymentModes.push({
          id: changeMode.id,
          name: changeMode.name,
          amount: Number(-calculatedChange),
        });
      }
    }

    return {
      due: calculatedDue,
      change: calculatedChange,
      prepaid: calculatedPrepaid,
      payment_modes: finalPaymentModes,
    };
  };

  return calculatePaymentSummary;
}

function applyOverallDiscountToUpdatedSale(
  result,
  finalItems,
  discount,
  finalOrderType,
  resolvedCharges,
  selectedChargeGroupIds,
  calculatePaymentSummary,
) {
  if (discount <= 0) return result;

  const { items: discountedItems, extraDiscount } = spreadSaleDiscount(
    finalItems,
    discount,
    finalOrderType,
    resolvedCharges,
    selectedChargeGroupIds,
  );

  const isNoSpread = ShopsHelper.isNoDiscountSpread();

  const finalTotals = calculateSaleSummary(discountedItems, {
    charges: [],
    saleLevelChargeGroupIds: result.saleLevelChargeGroupIds,
    existingSaleCharges: result?.charges,
    extraDiscount,
    orderType: finalOrderType,
    isDiscountSpread: !isNoSpread,
  });

  const discountFinancials = calculatePaymentSummary(finalTotals.payable);

  return {
    ...result,
    items: discountedItems,
    payable: finalTotals.payable,
    roundOff: finalTotals.roundOff,
    total: finalTotals.total,
    tax: finalTotals.tax,
    cess: finalTotals.cess,
    discount_applied_on_items: finalTotals.discount_applied_on_items,
    discount: finalTotals.discount,
    extraDiscount: finalTotals.extraDiscount ?? extraDiscount ?? 0,
    normal_payable: finalTotals.normal_payable,
    charges: finalTotals.charges,
    due: discountFinancials.due,
    change: discountFinancials.change,
    prepaid: discountFinancials.prepaid,
    payment_modes: discountFinancials.payment_modes,
    totalChargeAmount: finalTotals.totalChargeAmount,
    totalSaleChargeAmount: finalTotals.totalSaleChargeAmount,
  };
}

function buildPaymentEntries(paymentModes = [], balancePayments = []) {
  const tenderData = (paymentModes || [])
    .filter(
      (m) =>
        Number(m.amount) > 0 &&
        m.name !== PAYMENT_MODES.CREDIT &&
        m.name !== PAYMENT_MODES.DISCOUNT &&
        m.name !== PAYMENT_MODES.DISCOUNT_PERCENTAGE &&
        m.name !== PAYMENT_MODES.POINTS,
    )
    .map((m) => ({ id: m.id, name: m.name, amount: Number(m.amount) }));

  const balanceFromPayments = (balancePayments || [])
    .filter((m) => Number(m.amount) > 0)
    .map((m) => ({ id: m.id, name: m.name, amount: Number(-m.amount) }));

  const balanceFromModes =
    balanceFromPayments.length === 0
      ? (paymentModes || [])
          .filter((m) => Number(m.amount) < 0)
          .map((m) => ({ id: m.id, name: m.name, amount: Number(m.amount) }))
      : [];

  const balanceEntries = [...balanceFromPayments, ...balanceFromModes];

  return { tenderData, balanceEntries };
}

function buildUpdatedSaleResult(sale, totals, baseFinancials, finalItems, finalOrderType, opts) {
  const {
    customerCreditUsed,
    loyaltyPointsUsed,
    loyaltyPointsAmount,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    updatedCustomer,
    token_no,
    shift,
  } = opts;
  return {
    ...sale,
    selectedChargeGroups: sale.selectedChargeGroups || [],
    total: totals.total,
    payable: totals.payable,
    normal_payable: totals.normal_payable,
    roundOff: totals.roundOff,
    cess: totals.cess,
    tax: totals.tax,
    payment_modes: baseFinancials.payment_modes,
    discount_applied_on_items: totals.discount_applied_on_items,
    discount: totals.discount,
    extraDiscount: totals.extraDiscount || 0,
    customer_credit_used: Number(customerCreditUsed || 0),
    loyalty_points_used: Number(loyaltyPointsUsed || 0),
    loyalty_points_amount: Number(loyaltyPointsAmount || 0),
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    due: baseFinancials.due,
    change: baseFinancials.change,
    prepaid: baseFinancials.prepaid,
    items: finalItems,
    customer: updatedCustomer || sale?.customer || null,
    token_no: token_no ?? sale?.token_no ?? null,
    order_type: finalOrderType,
    shift: shift ?? sale?.shift ?? null,
    time: sale?.time,
    totalChargeAmount: totals.totalChargeAmount,
    totalSaleChargeAmount: totals.totalSaleChargeAmount,
    charges: sale?.charges || [],
  };
}

async function prepareSaleContext({ sale, orderType, charges }) {
  const resolvedCharges = charges?.length ? charges : (await ProductHelper.getChargesFromDB()) || [];

  const finalOrderType = orderType !== undefined ? orderType : (sale?.order_type ?? null);

  const finalItems = await prepareUpdatedSaleItems(sale, finalOrderType, resolvedCharges);

  return {
    resolvedCharges,
    finalOrderType,
    finalItems,
  };
}

function preparePaymentContext({ paymentModes = [], balancePayments = [], discount, selectedCustomer }) {
  const hasValidCustomer = !!selectedCustomer?.id;

  const filteredPaymentModes = hasValidCustomer
    ? paymentModes || []
    : (paymentModes || []).filter((m) => m.name !== PAYMENT_MODES.CREDIT && m.name !== PAYMENT_MODES.POINTS);

  const creditEntry = filteredPaymentModes.find((m) => m.name === PAYMENT_MODES.CREDIT);

  const balanceCreditEntry = (balancePayments || []).find((m) => m.name === PAYMENT_MODES.CREDIT);

  const customerCreditUsed = hasValidCustomer
    ? Number(creditEntry?.amount || 0) - Number(balanceCreditEntry?.amount || 0)
    : 0;

  const { tenderData, balanceEntries } = buildPaymentEntries(filteredPaymentModes, balancePayments);

  const calculatePaymentSummary = createPaymentSummary(filteredPaymentModes, discount, tenderData, balanceEntries);

  return {
    customerCreditUsed,
    calculatePaymentSummary,
  };
}

async function prepareRecalculatedItems({ sale, finalItems, finalOrderType, resolvedCharges }) {
  const { selectedChargeGroupIds, saleLevelChargeGroupIds } = getActiveChargeGroupIds({
    ...sale,
    items: finalItems,
    order_type: finalOrderType,
  });

  const recalculatedItems = await recalculateSaleItems(
    {
      ...sale,
      items: finalItems,
      order_type: finalOrderType,
    },
    selectedChargeGroupIds,
    resolvedCharges,
  );

  return {
    recalculatedItems,
    selectedChargeGroupIds,
    saleLevelChargeGroupIds,
  };
}

async function prepareUpdatedCustomer(selectedCustomer, customerCreditUsed, previousCreditUsed = 0) {
  const creditUsed = Number(customerCreditUsed || 0);
  const prevCredit = Number(previousCreditUsed || 0);
  const creditDifference = creditUsed - prevCredit;

  if (!selectedCustomer || creditDifference === 0) {
    return selectedCustomer;
  }

  return {
    ...selectedCustomer,
    balance: Number(selectedCustomer.balance || 0) - creditDifference,
  };
}

function calculateUpdatedSaleTotals({
  sale,
  recalculatedItems,
  resolvedCharges,
  finalOrderType,
  saleLevelChargeGroupIds,
  selectedChargeGroupIds,
}) {
  return calculateSaleSummary(recalculatedItems, {
    charges: resolvedCharges,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    existingSaleCharges: sale?.charges,
    orderType: finalOrderType,
  });
}

function createUpdatedSaleResult({
  sale,
  totals,
  calculatePaymentSummary,
  recalculatedItems,
  finalOrderType,
  customerCreditUsed,
  loyaltyPointsUsed,
  loyaltyPointsAmount,
  saleLevelChargeGroupIds,
  selectedChargeGroupIds,
  updatedCustomer,
  token_no,
  shift,
}) {
  const baseFinancials = calculatePaymentSummary(totals.payable);

  return buildUpdatedSaleResult(sale, totals, baseFinancials, recalculatedItems, finalOrderType, {
    customerCreditUsed,
    loyaltyPointsUsed,
    loyaltyPointsAmount,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    updatedCustomer,
    token_no,
    shift,
  });
}

async function prepareUpdatedSaleData({
  sale,
  orderType,
  charges,
  paymentModes,
  balancePayments,
  discount,
  selectedCustomer,
  previousCreditUsed,
}) {
  const { resolvedCharges, finalOrderType, finalItems } = await prepareSaleContext({
    sale,
    orderType,
    charges,
  });

  const { customerCreditUsed, calculatePaymentSummary } = preparePaymentContext({
    paymentModes,
    balancePayments,
    discount,
    selectedCustomer,
  });

  let prevCredit = previousCreditUsed;
  if (prevCredit === undefined && sale?.editing_sale_local_id) {
    const originalSale = await getSaleById(sale.editing_sale_local_id);
    prevCredit = Number(originalSale?.customer_credit_used || 0);
  }

  const updatedCustomer = await prepareUpdatedCustomer(selectedCustomer, customerCreditUsed, prevCredit || 0);

  return {
    resolvedCharges,
    finalOrderType,
    finalItems,
    customerCreditUsed,
    calculatePaymentSummary,
    updatedCustomer,
  };
}

async function prepareSaleCalculation({ sale, finalItems, finalOrderType, resolvedCharges }) {
  const { recalculatedItems, selectedChargeGroupIds, saleLevelChargeGroupIds } = await prepareRecalculatedItems({
    sale,
    finalItems,
    finalOrderType,
    resolvedCharges,
  });

  const totals = calculateUpdatedSaleTotals({
    sale,
    recalculatedItems,
    resolvedCharges,
    finalOrderType,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
  });

  return {
    recalculatedItems,
    selectedChargeGroupIds,
    saleLevelChargeGroupIds,
    totals,
  };
}
export const buildUpdatedSale = async (params) => {
  const saleData = await prepareUpdatedSaleData(params);

  const calculation = await prepareSaleCalculation({
    sale: params.sale,
    finalItems: saleData.finalItems,
    finalOrderType: saleData.finalOrderType,
    resolvedCharges: saleData.resolvedCharges,
  });

  const result = createUpdatedSaleResult({
    sale: params.sale,
    totals: calculation.totals,
    calculatePaymentSummary: saleData.calculatePaymentSummary,
    recalculatedItems: calculation.recalculatedItems,
    finalOrderType: saleData.finalOrderType,
    customerCreditUsed: saleData.customerCreditUsed,
    loyaltyPointsUsed: params.loyaltyPointsUsed,
    loyaltyPointsAmount: params.loyaltyPointsAmount,
    saleLevelChargeGroupIds: calculation.saleLevelChargeGroupIds,
    selectedChargeGroupIds: calculation.selectedChargeGroupIds,
    updatedCustomer: saleData.updatedCustomer,
    token_no: params.token_no,
    shift: params.shift,
  });

  const rawManualDiscount = Number(params.discount ?? params.sale?.extraDiscount ?? 0);

  const chargeDiscountTotal = getSaleChargeDiscountTotal(calculation.totals?.charges || []);

  const manualDiscountToApply =
    chargeDiscountTotal > 0 && rawManualDiscount === Number(params.sale?.discount || 0)
      ? Number(params.sale?.extraDiscount || 0)
      : rawManualDiscount;

  const isNoSpread = ShopsHelper.isNoDiscountSpread();
  const totalDiscountToSpread = isNoSpread ? manualDiscountToApply : chargeDiscountTotal + manualDiscountToApply;

  return applyOverallDiscountToUpdatedSale(
    result,
    calculation.recalculatedItems,
    totalDiscountToSpread,
    saleData.finalOrderType,
    saleData.resolvedCharges,
    calculation.selectedChargeGroupIds,
    saleData.calculatePaymentSummary,
  );
};

function applyDiscountToItemInSpread(item, currentItemAppliedDiscount, orderType, selectedChargeGroupIds, charges) {
  const taxPreference = TaxHelper.getTaxPreference();
  const taxPercentage = Number(item.taxPercentage || 0);
  const cessPercentage = Number(item.cessPercentage || 0);
  const totalTaxPercentage = taxPercentage + cessPercentage;

  let taxInclusiveUnitRateBeforeDiscount = Number(item.rateAndTax ?? 0);
  if (!taxInclusiveUnitRateBeforeDiscount) {
    const baseRate = Number(item.rate ?? item.item?.price ?? item.price ?? 0);
    taxInclusiveUnitRateBeforeDiscount =
      taxPreference === TAX_PREFERENCES.EXCLUDING_TAX ? baseRate * (1 + totalTaxPercentage / 100) : baseRate;
  }
  const totalTaxInclusiveBeforeDiscount = taxInclusiveUnitRateBeforeDiscount * (item.qty || 1);

  const discountPercentage =
    totalTaxInclusiveBeforeDiscount > 0
      ? Math.min(
          100,
          Math.max(
            0,
            (item.discountPercentage || 0) + (currentItemAppliedDiscount / totalTaxInclusiveBeforeDiscount) * 100,
          ),
        )
      : item.discountPercentage || 0;

  return {
    ...item,
    ...CartHelper.getCartItemData(
      item.item,
      item,
      item.qty,
      {
        discountPercentage,
        orderType,
        selectedChargeGroupIds,
      },
      charges,
    ),
  };
}

export const spreadSaleDiscount = (
  items,
  totalDiscount,
  orderType = null,
  charges = [],
  selectedChargeGroupIds = null,
) => {
  let remainingDiscount = totalDiscount;
  const itemsToSpread = (items || []).map((item) => ({ ...item }));

  if (ShopsHelper.isNoDiscountSpread()) {
    return { items: itemsToSpread, extraDiscount: totalDiscount };
  }

  const sortedItems = [...itemsToSpread].sort((a, b) => {
    const taxA = Number(a.taxPercentage || 0) + Number(a.cessPercentage || 0);
    const taxB = Number(b.taxPercentage || 0) + Number(b.cessPercentage || 0);
    return taxB - taxA;
  });

  const totalPayableBeforeDiscount = itemsToSpread.reduce((sum, it) => sum + Number(it.payable || 0), 0);
  if (totalPayableBeforeDiscount <= 0) return { items: itemsToSpread, extraDiscount: remainingDiscount };

  for (let i = 0; i < sortedItems.length; i++) {
    const item = sortedItems[i];
    if (remainingDiscount <= 0) break;

    const currentItemPayable = Number(item.payable || 0);
    if (currentItemPayable <= 0) continue;

    let discountToApply = 0;
    if (i === sortedItems.length - 1) {
      discountToApply = Math.min(remainingDiscount, currentItemPayable);
    } else {
      discountToApply = (currentItemPayable / totalPayableBeforeDiscount) * totalDiscount;
      discountToApply = Math.min(remainingDiscount, discountToApply, currentItemPayable);
    }

    if (discountToApply > 0) {
      const currentItemAppliedDiscount = amountRoundOff(
        currentItemPayable - Math.max(0, currentItemPayable - discountToApply),
      );
      const updatedItem = applyDiscountToItemInSpread(
        item,
        currentItemAppliedDiscount,
        orderType,
        selectedChargeGroupIds,
        charges,
      );

      const itemIndex = itemsToSpread.findIndex((it) => it === item);
      if (itemIndex !== -1) {
        itemsToSpread[itemIndex] = updatedItem;
      }

      remainingDiscount = amountRoundOff(remainingDiscount - currentItemAppliedDiscount);
    }
  }

  return { items: itemsToSpread, extraDiscount: remainingDiscount };
};

export const spreadPointsReduction = (items, totalReduction) => {
  let remainingReduction = totalReduction;

  const itemsToReduce = (items || []).map((it) => ({ ...it }));

  const groups = {};
  itemsToReduce.forEach((item) => {
    const tax = Number(item.taxPercentage || 0);
    const cess = Number(item.cessPercentage || 0);
    const totalTax = tax + cess;
    if (!groups[totalTax]) groups[totalTax] = [];
    groups[totalTax].push(item);
  });

  const sortedTaxRates = Object.keys(groups)
    .map(Number)
    .sort((a, b) => b - a);

  for (const taxRate of sortedTaxRates) {
    if (remainingReduction <= 0) break;

    for (const item of groups[taxRate]) {
      if (remainingReduction <= 0) break;

      const currentItemPayable = Number(
        item.payable || Number(item.rate ?? item.item.price ?? 0) * Number(item.qty || 0),
      );
      if (currentItemPayable <= 0) continue;

      const reduction = Math.min(remainingReduction, currentItemPayable);

      if (reduction > 0) {
        const newPayable = currentItemPayable - reduction;

        const qty = Number(item.qty || 1);
        const taxPercent = Number(item.taxPercentage || 0);
        const cessPercent = Number(item.cessPercentage || item.cess || 0);
        const totalTaxPercent = taxPercent + cessPercent;

        const newRate = newPayable / (qty * (1 + totalTaxPercent / 100));
        item.rate = Number(newRate.toFixed(4));

        item.tax = Number(((item.rate * qty * taxPercent) / 100).toFixed(4));
        item.cess = Number(((item.rate * qty * cessPercent) / 100).toFixed(4));

        item.payable = Number((item.rate * qty + item.tax + item.cess).toFixed(4));

        remainingReduction -= reduction;
      }
    }
  }

  return itemsToReduce;
};

export const navigateToTableDashboard = (navigate) => {
  const terminalSettings = TerminalHelper.getTerminalSettings();
  const isEnabled = terminalSettings?.isTableDashboardEnabled;

  if (isEnabled && navigate) {
    navigate(ROUTES.TABLE_DASHBOARD);
  }
};

export const getInvoicePrinterFormat = () => {
  const settings = TerminalHelper.getTerminalSettings();
  return settings?.invoicePrinterFormat || INVOICE_FORMATS.A4;
};

export const formatSaleForAndroidResponse = (sale) => {
  if (!sale) return {};

  return {
    billNumber: sale.bare_bill_no,
    cess: sale.cess,
    createdAt: sale.time,
    discount: (Number(sale.discount_applied_on_items) || 0) + (Number(sale.discount) || 0),
    id: sale.local_id,
    items: (sale.items || []).map((item) => ({
      localId: item.localId || nanoid(),
      billLevelDiscount: 0.0,
      cess: item.cess,
      discount: item.discount,
      id: item.id,
      item: item.item,
      kotPrintedQty: item.qty,
      mrp: item.mrp || 0.0,
      notes: item.note || "",
      optionQty: item.qty,
      option: item.option,
      payable: item.payable,
      qty: item.qty,
      rate: item.rate ?? 0,
      rateAndTax: item.rateAndTax ?? item.rate ?? 0,
      rateWithoutDiscountSpread: item.rate ?? 0,
      status: item.status || KOT_STATUS.DRAFT,
      tax: item.tax,
    })),
    kotTable: sale.table,
    kotChair: sale.table?.chair || null,
    normal_payable: sale.normal_payable || 0.0,
    payable: sale.payable,
    roundOff: sale.roundOff || 0.0,
    token_no: sale.token_no,
    sale_status: sale.status || SALE_STATUS.PENDING,
    status: sale.kot_status || KOT_STATUS.PENDING,
    kot_status: sale.kot_status || KOT_STATUS.PENDING,
    order_type: sale.order_type,
    tax: sale.tax,
    waiter: sale.waiter,
  };
};

async function mergeNewItemIntoCurrentItems(
  currentItems,
  newItem,
  existingSale,
  selectedChargeGroupIds,
  options,
  priceCategory = null,
  priceCategoryItems = [],
) {
  const existingItemIndex = currentItems.findIndex(
    (existingItem) =>
      existingItem.id === newItem.id && (existingItem.option?.label || null) === (newItem.option?.label || null),
  );

  if (existingItemIndex > -1) {
    const existingItem = currentItems[existingItemIndex];
    const product = await ProductHelper.getProductById(existingItem.id);

    const updatedNote = existingItem.note
      ? newItem.note
        ? `${existingItem.note}, ${newItem.note}`
        : existingItem.note
      : newItem.note;

    if (product) {
      const newQty = amountRoundOff(existingItem.qty + newItem.qty);
      const itemRate = resolveEffectiveItemRate(product, newQty, priceCategory, priceCategoryItems, existingItem);
      const updatedItem = CartHelper.getCartItemData(
        product,
        existingItem,
        newQty,
        {
          orderType: existingSale?.order_type,
          selectedChargeGroupIds,
          rate: itemRate,
        },
        options.charges,
      );
      updatedItem.note = updatedNote;
      updatedItem.latestNote = newItem.note;
      currentItems[existingItemIndex] = updatedItem;
    } else {
      existingItem.qty = amountRoundOff(existingItem.qty + newItem.qty);
      existingItem.payable = amountRoundOff(existingItem.price * existingItem.qty);
      existingItem.note = updatedNote;
      existingItem.latestNote = newItem.note;
    }
  } else {
    currentItems.push({
      ...newItem,
      kotPrintedQty: 0,
    });
  }
}

export const addItemsToExistingSale = async (salesId, newItemsInput, options = {}) => {
  const existingSale = await getSaleById(salesId);

  if (!existingSale) {
    throw new Error(`Sale with ID ${salesId} not found`);
  }
  if (existingSale.status !== SALE_STATUS.DRAFT) {
    throw new Error(`Sale with ID ${salesId} is not in ${existingSale.status} status`);
  }

  if (options.orderType) {
    existingSale.order_type = options.orderType;
  }

  const fetchedProducts = await Promise.all(newItemsInput.map((item) => ProductHelper.getProductById(item.id)));
  const tempItems = [
    ...(existingSale.items || []),
    ...newItemsInput.map((saleItem, idx) => ({ item: fetchedProducts[idx] })),
  ];

  const { selectedChargeGroupIds } = getActiveChargeGroupIds({
    ...existingSale,
    items: tempItems,
  });

  const priceCategoryItems = existingSale?.price_category ? await ProductHelper.getPriceCategoryItemsFromDB() : [];

  const calculatedNewItems = newItemsInput.map((saleItem, idx) => {
    const product = fetchedProducts[idx];
    if (!product) {
      return { ...saleItem, qty: saleItem.qty || saleItem.quantity, localId: saleItem.localId || nanoid() };
    }

    const quantity = amountRoundOff(saleItem.qty || saleItem.quantity || 1);
    const itemRate = resolveEffectiveItemRate(
      product,
      quantity,
      existingSale?.price_category,
      priceCategoryItems,
      saleItem,
    );

    const data = CartHelper.getCartItemData(
      product,
      saleItem,
      quantity,
      {
        orderType: existingSale?.order_type,
        selectedChargeGroupIds,
        rate: itemRate,
      },
      options.charges,
    );
    return {
      ...data,
      localId: saleItem.localId || nanoid(),
    };
  });

  const currentItems = [...(existingSale.items || [])];

  for (const newItem of calculatedNewItems) {
    await mergeNewItemIntoCurrentItems(
      currentItems,
      newItem,
      existingSale,
      selectedChargeGroupIds,
      options,
      existingSale?.price_category,
      priceCategoryItems,
    );
  }

  const updatedSale = recalculateSale(existingSale, currentItems, options);
  await saveSaleInDB(updatedSale, options);

  return { updatedSale, calculatedNewItems };
};

function mergeEditedPayloadItems(editedItemsInput) {
  const editedItems = Array.isArray(editedItemsInput) ? editedItemsInput : [editedItemsInput];
  const flatEditedItems = editedItems.flatMap((item) => item.items ?? [item]);
  const mergedPayloadItems = [];

  flatEditedItems.forEach((item) => {
    const productId = item.id;
    const optionLabel = item.option?.label || null;

    const existingIndex = mergedPayloadItems.findIndex((mergedItem) => {
      const mProductId = mergedItem.id;
      const mOptionLabel = mergedItem.option?.label || null;
      return String(mProductId) === String(productId) && mOptionLabel === optionLabel;
    });

    if (existingIndex > -1) {
      const existing = mergedPayloadItems[existingIndex];
      const newQty = Number(existing.qty || existing.quantity || 0) + Number(item.qty || item.quantity || 0);

      mergedPayloadItems[existingIndex] = {
        ...existing,
        qty: newQty,
        quantity: newQty,
      };
    } else {
      mergedPayloadItems.push({ ...item });
    }
  });

  return mergedPayloadItems;
}

export const editSaleItems = async (salesId, editedItemsInput, options = {}) => {
  const existingSale = await getSaleById(salesId);

  if (!existingSale) {
    throw new Error(`Sale with ID ${salesId} not found`);
  }
  if (existingSale.status !== SALE_STATUS.DRAFT) {
    throw new Error(`Sale with ID ${salesId} is not in ${existingSale.status} status`);
  }

  if (options.orderType) {
    existingSale.order_type = options.orderType;
  }

  const mergedPayloadItems = mergeEditedPayloadItems(editedItemsInput);
  const fetchedProducts = await Promise.all(mergedPayloadItems.map((item) => ProductHelper.getProductById(item.id)));

  const tempItems = mergedPayloadItems.map((editedItem, idx) => ({ item: fetchedProducts[idx] }));

  const { selectedChargeGroupIds } = getActiveChargeGroupIds({
    ...existingSale,
    items: tempItems,
  });

  const existingItems = existingSale.items || [];
  const deletedItems = [...(existingSale.deletedItems || [])];

  const updatedItems = mergedPayloadItems
    .map((editedItem, idx) => {
      const product = fetchedProducts[idx];
      if (!product) return null;

      const newQty = amountRoundOff(editedItem.qty || editedItem.quantity || 0);
      if (newQty <= 0) return null;

      const matchedExisting = existingItems.find(
        (existingItem) =>
          String(existingItem.id) === String(editedItem.id) &&
          (editedItem.option?.label || null) === (existingItem.option?.label || null),
      );

      const data = CartHelper.getCartItemData(
        product,
        editedItem,
        newQty,
        { orderType: existingSale?.order_type, selectedChargeGroupIds },
        options.charges,
      );
      return {
        ...data,
        localId: matchedExisting?.localId || nanoid(),
        kotPrintedQty: matchedExisting?.kotPrintedQty || 0,
      };
    })
    .filter(Boolean);

  existingItems.forEach((existingItem) => {
    const isPresent = updatedItems.some(
      (updatedItem) =>
        String(updatedItem.id) === String(existingItem.id) &&
        (updatedItem.option?.label || null) === (existingItem.option?.label || null),
    );

    if (!isPresent && (existingItem.kotPrintedQty || 0) > 0) {
      deletedItems.push({
        ...existingItem,
        kotPrintedQty: existingItem.kotPrintedQty,
        newQty: 0,
        qty: existingItem.kotPrintedQty,
      });
    }
  });

  const saleWithDeletedItems = {
    ...existingSale,
    deletedItems,
  };

  const updatedSale = recalculateSale(saleWithDeletedItems, updatedItems, options);

  await saveSaleInDB(updatedSale, options);

  return { updatedSale };
};

/**
 * Concurrency Lock:
 * When KDS accepts an order or toggles items in bulk, it fires parallel PATCH requests (Promise.all).
 * Without serialization, concurrent reads on the same sale snapshot in IndexedDB cause later writes to overwrite earlier updates, reverting items back to their previous status (e.g. DRAFT).
 * This queue ensures updates for the same saleId are processed sequentially.
 */
const cartItemUpdateQueues = new Map();

export const updateCartItemStatus = async (saleId, itemId, status, option, allItems = false) => {
  const previousPromise = cartItemUpdateQueues.get(saleId) || Promise.resolve();

  const currentPromise = previousPromise.then(async () => {
    const sale = await getSaleById(saleId);

    if (!sale) throw new Error("Sale not found");
    if (sale.status !== SALE_STATUS.DRAFT) {
      throw new Error(`Sale with ID ${saleId} is not in ${sale.status} status`);
    }

    let updated = false;
    let allItemsReady = true;

    const updatedItems = (sale.items || []).map((item) => {
      const isSameItem = String(item.id) === String(itemId);
      const isSameOption = (item.option?.label || null) === (option?.label || null);

      let newItemStatus = item.status;

      if (allItems || (isSameItem && isSameOption)) {
        updated = true;
        newItemStatus = status;
      }

      if (newItemStatus !== KOT_STATUS.READY) {
        allItemsReady = false;
      }

      return { ...item, status: newItemStatus };
    });

    if (!updated) {
      throw new Error("Matching item not found");
    }

    let newOrderStatus = sale.kot_status;
    if (status === KOT_STATUS.READY) {
      if (newOrderStatus === KOT_STATUS.DRAFT || newOrderStatus === KOT_STATUS.PENDING) {
        newOrderStatus = KOT_STATUS.PREPARING;
      }
      if (allItemsReady) {
        newOrderStatus = KOT_STATUS.READY;
      }
    } else if (status === KOT_STATUS.PREPARING) {
      if (newOrderStatus === KOT_STATUS.DRAFT || newOrderStatus === KOT_STATUS.PENDING) {
        newOrderStatus = KOT_STATUS.PREPARING;
      }
    }

    const updatedSale = { ...sale, items: updatedItems, kot_status: newOrderStatus };
    await saveSaleInDB(updatedSale, { notify: true });

    return updatedSale;
  });

  cartItemUpdateQueues.set(
    saleId,
    currentPromise.catch(() => {}),
  );

  return currentPromise;
};

export const updateSaleCustomer = async (saleId, customer, options = {}) => {
  const sale = await getSaleById(saleId);
  if (!sale) return null;

  const resolvedCustomer = await fetchCustomer(customer);

  const updatedSale = {
    ...sale,
    customer: resolvedCustomer,
  };

  await saveSaleInDB(updatedSale, options);

  return updatedSale;
};

export const updateSaleTokenNumber = async (saleId, tokenNumber, options = {}) => {
  const sale = await getSaleById(saleId);
  if (!sale) return null;

  const updatedSale = {
    ...sale,
    token_no: tokenNumber,
  };

  await saveSaleInDB(updatedSale, options);

  return updatedSale;
};

const recalculateItemsForPriceCategory = (items, priceCategory, priceCategoryItems, orderType, charges) => {
  if (!items || items.length === 0) return [];
  return items.map((saleItem) => {
    const cloned = { ...saleItem };
    calculateSaleItemSummary(cloned, {
      orderType,
      charges,
      priceCategory,
      priceCategoryItems,
    });
    return cloned;
  });
};

export const changeTable = async (saleId, tableId, chairId = null, options = {}) => {
  const sale = await getSaleById(saleId);

  if (!sale) throw new Error("Sale not found");
  if (sale.status !== SALE_STATUS.DRAFT) {
    throw new Error(`Sale with ID ${saleId} is not in ${sale.status} status`);
  }

  const tableInfo = await resolveTableAreaDetails(tableId, chairId);
  if (!tableInfo) throw new Error("Table not found");

  const newPriceCategory = tableInfo?.area?.price_category || null;
  const priceCategoryItems = newPriceCategory ? await ProductHelper.getPriceCategoryItemsFromDB() : [];
  const updatedItems = recalculateItemsForPriceCategory(
    sale.items,
    newPriceCategory,
    priceCategoryItems,
    sale.order_type,
    options.charges || [],
  );

  const { selectedChargeGroupIds, saleLevelChargeGroupIds } = getActiveChargeGroupIds({
    ...sale,
    table: tableInfo,
    items: updatedItems,
  });

  const totals = calculateSaleSummary(updatedItems, {
    charges: options.charges || [],
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    orderType: sale.order_type,
    extraDiscount: sale.extraDiscount || 0,
  });

  const dueAndChange = calculateDueAndChange(sale, totals.payable);

  const updatedSale = {
    ...sale,
    table: tableInfo,
    price_category: newPriceCategory,
    items: updatedItems,
    selectedChargeGroupIds,
    saleLevelChargeGroupIds,
    ...totals,
    ...dueAndChange,
  };

  await saveSaleInDB(updatedSale, options);
  WebSocketGatewayService.broadcast({ action: "TABLE_CHANGED" });
  return updatedSale;
};

function performSaleItemsSplit(itemsToSplit, remainingItems, originalSale, options) {
  const newSaleItems = [];

  for (const input of itemsToSplit) {
    const parentItem = remainingItems.find(
      (item) =>
        (String(item.id) === String(input.itemId || input.id) ||
          (input.localId && String(item.localId) === String(input.localId))) &&
        (item.option?.label || null) === (input.option?.label || null),
    );

    if (!parentItem) continue;

    const splitQty = Number(input.quantity ?? input.qty ?? input.splitQty);
    const parentQty = Number(parentItem.qty);

    if (splitQty <= 0 || splitQty > parentQty) continue;

    const newItem = {
      ...parentItem,
      qty: splitQty,
    };
    calculateSaleItemSummary(newItem, { orderType: originalSale?.order_type, charges: options.charges });
    newSaleItems.push(newItem);

    if (splitQty === parentQty) {
      const index = remainingItems.indexOf(parentItem);
      remainingItems.splice(index, 1);
    } else {
      parentItem.qty = parentQty - splitQty;
      calculateSaleItemSummary(parentItem, { orderType: originalSale?.order_type, charges: options.charges });
    }
  }

  return newSaleItems;
}

export const splitSale = async (saleId, itemsToSplit, options = {}) => {
  const originalSale = await getSaleById(saleId);

  if (!originalSale) throw new Error("Sale not found");
  if (originalSale.status !== SALE_STATUS.DRAFT) {
    throw new Error(`Sale with ID ${saleId} is not in ${originalSale.status} status`);
  }

  if (!itemsToSplit || itemsToSplit.length === 0) throw new Error("Items should not be empty");

  const remainingItems = [...originalSale.items];
  const newSaleItems = performSaleItemsSplit(itemsToSplit, remainingItems, originalSale, options);

  if (newSaleItems.length === 0) throw new Error("No valid items selected to split");

  const updatedOriginal = recalculateSale(originalSale, remainingItems, options);
  await saveSaleInDB(updatedOriginal, options);

  const currentShiftId = ShiftHelper.getCurrentShiftId();
  const newSaleTotals = calculateSaleSummary(newSaleItems, { ...options, orderType: originalSale?.order_type });

  const newSale = {
    items: newSaleItems,
    waiter: originalSale.waiter,
    table: originalSale.table,
    payment_modes: [],
    ...newSaleTotals,
    shift: currentShiftId,
    time: originalSale.time,
    status: SALE_STATUS.DRAFT,
    cashier: originalSale.cashier,
  };

  await createNewSaleInDB(newSale, options);
  return { original: updatedOriginal, new: newSale };
};

export const mergeSalesId = async (saleIds, options = {}) => {
  if (!saleIds || saleIds.length < 2) throw new Error("Need at least 2 sales to merge");

  const salesToMerge = (await Promise.all(saleIds.map((id) => getSaleById(id)))).filter(Boolean);

  if (salesToMerge.length !== saleIds.length) throw new Error("Some sales not found");

  return await mergeSales(salesToMerge, options);
};

export const mergeSales = async (salesToMerge, options = {}) => {
  if (!salesToMerge || salesToMerge.length < 2) throw new Error("Need at least 2 sales to merge");

  for (const sale of salesToMerge) {
    if (sale.status !== SALE_STATUS.DRAFT) {
      throw new Error(`Sale with ID ${sale.bill_no || sale.local_id} is not in ${sale.status} status`);
    }
  }

  const mergedItems = combineDeduplicatedSaleItems(salesToMerge);
  const currentShiftId = ShiftHelper.getCurrentShiftId();
  const baseSale = salesToMerge[0];

  const updatedSale = {
    ...baseSale,
    table: options.targetTable || baseSale.table,
    items: mergedItems,
    time: DateTimeHelpers.getLocalISOString(),
    status: SALE_STATUS.DRAFT,
    shift: currentShiftId,
    payment_modes: [],
  };

  const mergedSale = recalculateSale(updatedSale, mergedItems, options);

  for (const sale of salesToMerge.slice(1)) {
    if (sale.local_id) {
      await deleteSale(sale.local_id, { ...options, notify: false });
      await ShiftHelper.deleteShiftPaymentsForSale(sale.local_id);
    }
  }

  await saveSaleInDB(mergedSale, { ...options, notify: options.notify ?? true });

  return mergedSale;
};

export const applyPaymentToSale = async (sale, tenders, due, change, _options = {}) => {
  const paymentModes = (tenders || []).map((mode) => {
    if (mode.payment_mode) {
      return {
        ...mode,
        id: mode.payment_mode.id,
        name: mode.payment_mode.name,
      };
    }
    return mode;
  });

  if (paymentModes.length === 0) {
    return {
      ...sale,
      due: sale.payable,
      change: 0,
      payment_modes: [],
    };
  } else {
    const totalPaid = paymentModes.reduce((sum, pm) => sum + (Number(pm.amount) || 0), 0);
    const finalDue = amountRoundOff(due ?? Math.max(sale.payable - totalPaid, 0));
    const finalChange = amountRoundOff(change ?? Math.max(totalPaid - sale.payable, 0));

    return buildUpdatedSale({
      sale: { ...sale },
      paymentModes,
      due: finalDue,
      change: finalChange,
      selectedCustomer: sale.customer,
      discount: 0,
    });
  }
};

const updateSaleStatus = async (sale, newStatus) => {
  const updatedSale = { ...sale, status: newStatus };

  // Update DB only if already saved in DB
  if (sale.local_id) {
    await saveSaleInDB(updatedSale);
  }

  return updatedSale;
};

export const saveAndProcess = async (sale, options = {}) => {
  const {
    invoiceTargetType,
    invoiceTargetValue,
    shop: shopFromOptions,
    fallbackPrintFunction,
    invoiceAction,
    customer,
    titles = [],
  } = options;

  try {
    if (!sale) return;

    const shop = shopFromOptions || (await ShopService.loadShopProfile());

    let updatedSale = sale;
    try {
      updatedSale = await saveSaleToDb(sale, null);
    } catch (err) {
      console.error("Failed to assign bill number in OrderManager:", err);
      toast.error("Failed to save sale!");
      throw err;
    }

    await ProductHelper.updateLocalStockForSale(updatedSale);

    const printParams = {
      sale: updatedSale,
      shop,
      fallbackPrintFunction,
      invoiceTargetType,
      invoiceTargetValue,
      titles,
    };

    await dispatchInvoiceAction(invoiceAction, printParams, updatedSale, shop, customer);

    const finalStatus = updatedSale.due === 0 ? SALE_STATUS.PAID : SALE_STATUS.PRINTED;
    if (finalStatus === SALE_STATUS.PAID && updatedSale.kot_status === KOT_STATUS.DRAFT) {
      updatedSale.kot_status = KOT_STATUS.DISPATCH;
    }
    const finalSale = await updateSaleStatus(updatedSale, finalStatus);

    return finalSale;
  } catch (error) {
    console.error("Failed to print invoice:", error);
    throw error;
  }
};

function executeWhatsAppShare(sale, shop) {
  try {
    InvoiceActionHelper.shareInvoiceOnWhatsApp(sale, shop);
  } catch (error) {
    console.warn("WhatsApp skip:", error?.message || error);
  }
}

const executePrintInvoice = async (params) => {
  try {
    await printInvoice(params);
  } catch (error) {
    if (error?.message?.includes("nodevicefound")) {
      toast.error("No printer found! Added to network queue.");
    } else {
      console.error(error?.message || "Failed to print invoice.");
    }
  }
};

const dispatchInvoiceAction = async (invoiceAction, printParams, finalizedSale, shop, customer) => {
  switch (invoiceAction) {
    case INVOICE_ACTIONS.PRINT:
      await executePrintInvoice(printParams);
      break;
    case INVOICE_ACTIONS.SAVE:
      break;
    case INVOICE_ACTIONS.WITH_KOT:
      await executePrintInvoice(printParams);
      await KotManager.printKot(printParams);
      break;
    case INVOICE_ACTIONS.PRINT_AND_WHATSAPP:
      await executePrintInvoice(printParams);
      executeWhatsAppShare(finalizedSale, shop, customer);
      break;
    case INVOICE_ACTIONS.SAVE_AND_WHATSAPP:
      executeWhatsAppShare(finalizedSale, shop, customer);
      break;
    case INVOICE_ACTIONS.SHARE:
      executeWhatsAppShare(finalizedSale, shop, customer);
      break;
    default:
      await executePrintInvoice(printParams);
      break;
  }
};

async function generateInvoiceBytes(invoiceFormat, sale, shop, titles) {
  if (invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE) {
    return await InvoiceHelper.getMultiLanguageThreeInchInvoice(sale, shop, titles);
  }
  if (invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC) {
    return await InvoiceHelper.getMultiLanguageThreeInchInvoice(sale, shop, titles, { isArabic: true });
  }
  return await InvoiceHelper.getThreeInchInvoice(sale, shop, titles);
}

function getPrinterConfig(terminalSettings, targetType, targetValue) {
  let { printerType, printerTarget } = TerminalHelper.getDefaultPrinterConfig(terminalSettings);

  if (targetType === INVOICE_TARGET_TYPE.IP && targetValue) {
    printerType = PRINTER_TYPES.LAN;
    printerTarget = targetValue;
  } else if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
    if (!printerTarget || printerTarget.toLowerCase() === "default") {
      printerTarget = null;
    }
  }

  return { printerType, printerTarget };
}

export const printInvoice = async (params = {}) => {
  const { sale, shop, fallbackPrintFunction, invoiceTargetType, invoiceTargetValue, titles = [] } = params;

  try {
    const terminalSettings = TerminalHelper.getTerminalSettings();

    if (sale.status === SALE_STATUS.PRINTED && !terminalSettings.printOnPayment) {
      return;
    }

    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    const bridge = AppBridgeHelper.getBridge();
    if (!bridge || invoiceFormat === INVOICE_FORMATS.A4 || invoiceFormat === INVOICE_FORMATS.A5) {
      await fallbackPrintFunction(sale);
      return;
    }

    const targetType = invoiceTargetType || localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_TYPE);
    const targetValue = invoiceTargetValue || localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_VALUE);

    if (targetType === INVOICE_TARGET_TYPE.NONE) return;

    const invoiceContent = await generateInvoiceBytes(invoiceFormat, sale, shop, titles);
    const base64invoice = bytesToBase64(invoiceContent);
    const { printerType, printerTarget } = getPrinterConfig(terminalSettings, targetType, targetValue);

    await PrinterManager.print({
      content: base64invoice,
      printerType,
      printerTarget,
      label: `INVOICE ${sale.bill_no}`,
    });
  } catch (err) {
    console.error("Print Invoice failed:", err);
  }
};

export const printCustomerPaymentReceipt = async ({
  payment,
  shop,
  fallbackPrintFunction,
  invoiceTargetType,
  invoiceTargetValue,
}) => {
  try {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    if (invoiceFormat === INVOICE_FORMATS.A4 || invoiceFormat === INVOICE_FORMATS.A5) {
      if (fallbackPrintFunction) await fallbackPrintFunction(payment, invoiceFormat);
      return;
    }

    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) {
      if (fallbackPrintFunction) await fallbackPrintFunction(payment, invoiceFormat);
      return;
    }

    const targetType = invoiceTargetType || localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_TYPE);
    const targetValue = invoiceTargetValue || localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_VALUE);

    if (targetType === INVOICE_TARGET_TYPE.NONE) {
      return;
    }

    let invoiceContent;
    if (invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE) {
      invoiceContent = await InvoiceHelper.getMultiLanguageThreeInchCustomerPaymentReceipt(payment, shop);
    } else if (invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC) {
      invoiceContent = await InvoiceHelper.getMultiLanguageThreeInchCustomerPaymentReceipt(payment, shop, {
        isArabic: true,
      });
    } else {
      invoiceContent = await InvoiceHelper.getThreeInchCustomerPaymentReceipt(payment, shop);
    }
    const base64invoice = bytesToBase64(invoiceContent);
    const { printerType, printerTarget } = getPrinterConfig(terminalSettings, targetType, targetValue);

    await PrinterManager.print({
      content: base64invoice,
      printerType,
      printerTarget,
      label: `PAYMENT RECEIPT: ${payment.id}`,
    });
  } catch (err) {
    console.error("Print Payment Receipt failed:", err);
  }
};

/**
 * Gets the existing sale items for a given product and option.
 * @param {Array} activeSaleItems - The list of items currently in the active sale.
 * @param {Object} item - The product item being added.
 * @param {Object} option - The selected product option (if any).
 * @returns {Array} An array of matching existing sale items.
 */
export const getExistingSaleItems = (activeSaleItems, product, option) => {
  return activeSaleItems.filter(
    (it) => it.item?.id === product?.id && (it.option?.label || null) === (option?.label || null),
  );
};

/**
 * Creates the data structure for a new standard sale item row.
 * @param {Object} item - The product item.
 * @param {number} qty - The quantity being added.
 * @param {string} note - Any special notes for this item.
 * @param {number} price - The effective price for the item.
 * @param {Object} option - The selected product option (if any).
 * @param {number|null} maxQty - The maximum quantity allowed for an addon discount (null if unlimited/standard).
 * @returns {Object} The complete sale item object ready to be added to the cart.
 */
export const createNewStandardSaleItem = (item, qty, note, rate, option, maxQty) => {
  const saleItem = {
    localId: nanoid(),
    item,
    name: item.name,
    id: item.id,
    productId: item.id,
    qty: amountRoundOff(qty),
    rate: amountRoundOff(rate),
    kotPrintedQty: 0,
    discount: 0.0,
    discountPercentage: 0.0,
    billLevelDiscount: 0.0,
    taxPercentage: item.tax,
    cessPercentage: item.cess,
    note,
    option,
    maxQty,
  };
  if (option) {
    saleItem.optionQty = amountRoundOff((option?.quantityRatio ?? 1) * qty);
  }

  return saleItem;
};

/**
 * Identifies if the added item is eligible for an addon discount based on parents in the cart.
 * Collects matching addon mappings and calculates maximum discounted quantity caps and price reductions.
 * @param {Array} activeSaleItems - The list of items currently in the active sale.
 * @param {Array} addonMappings - The store's full list of addon configurations.
 * @param {Object} product - The product item being added.
 * @returns {Array} List of eligible addon discounts with their limits (`maxQty`) and `priceDelta`.
 */
export const getEligibleAddonSales = (activeSaleItems, addonMappings, product) => {
  const addonMappingsForProduct = addonMappings.filter((m) => m.addon_item === product.id && m.is_available);

  return addonMappingsForProduct
    .map((m) => {
      const parentSaleItems = activeSaleItems.filter((si) => si.productId === m.item);
      if (parentSaleItems.length === 0) return null;

      const totalParentQty = parentSaleItems.reduce((acc, si) => acc + si.qty, 0);

      return {
        parentSaleItems,
        maxQty: totalParentQty * m.max_quantity,
        priceDelta: m.price_delta,
      };
    })
    .filter(Boolean);
};

/**
 * Processes a standard item addition (non-discounted).
 * Either updates the quantity of an existing row or creates and pushes a new row if none exists.
 * @param {Object} params - Destructured parameters.
 * @param {Array} params.saleItems - The list of items currently in the active sale. Should make new changes in this object to update sale items.
 * @param {Object} params.product - The product item.
 * @param {number} params.qty - The quantity being added.
 * @param {string} params.note - Any special notes for this item.
 * @param {number} params.price - The effective price.
 * @param {Object} params.option - The selected product option (if any).
 * @returns {Object} The resulting sale item object that was updated or added.
 */
export const processStandardSaleItem = ({
  saleItems,
  product,
  qty,
  note,
  rate,
  option,
  orderType,
  charges = [],
  selectedChargeGroupIds = null,
  priceCategory = null,
  priceCategoryItems = [],
}) => {
  const existingProductSaleItems = getExistingSaleItems(saleItems, product, option);
  let saleItem = existingProductSaleItems[0] ?? null;
  if (!saleItem || ConfigHelper.getAddToCartOption() === ADD_TO_CART_OPTIONS.NEW_ITEM.value) {
    saleItem = createNewStandardSaleItem(product, qty, note, rate, option);
    if (priceCategory && rate != null) {
      saleItem.priceCategoryRate = rate;
    }
    saleItems.push(saleItem);
  } else {
    saleItem.qty = quantityRoundOff(saleItem.qty + qty);
    if (note) {
      saleItem.note = note;
    }
    saleItem.option = option;
    if (saleItem.manualRate == null && rate != null) {
      saleItem.rate = rate;
      if (priceCategory) {
        saleItem.priceCategoryRate = rate;
      }
    }
  }
  if (option) {
    saleItem.optionQty = quantityRoundOff(saleItem.optionQty + (option?.quantityRatio ?? 1) * qty);
  }

  calculateSaleItemSummary(saleItem, {
    orderType,
    charges,
    selectedChargeGroupIds,
    priceCategory,
    priceCategoryItems,
    rate,
  });

  return saleItem;
};

/**
 * Handles the complex logic of adding an item that is eligible for addon discounts.
 * Splits the requested quantity: applies the best discount up to the remaining eligible `maxQty`,
 * and pushes any overflow quantity as a standard, non-discounted row.
 * @param {Object} params - Destructured parameters.
 * @param {Array} params.saleItems - The list of items currently in the active sale. Should make new changes in this object to update sale items.
 * @param {Object} params.product - The product item being added.
 * @param {number} params.qty - The quantity being added.
 * @param {string} params.note - Any special notes.
 * @param {number} params.price - The effective price.
 * @param {Object} params.option - The selected product option (if any).
 * @param {Array} params.eligibleAddonSales - Array of eligible parent mapping limits generated from `getEligibleAddonSales`.
 */
function applyAddonDiscountedQtyItem(
  existingDiscountedSaleItem,
  product,
  applyQty,
  note,
  discountedRate,
  option,
  totalMaxQty,
  rate,
  orderType,
  charges,
  selectedChargeGroupIds,
) {
  let discountedSaleItem = null;
  if (!existingDiscountedSaleItem) {
    discountedSaleItem = createNewStandardSaleItem(product, applyQty, note, discountedRate, option, applyQty);
  } else {
    discountedSaleItem = { ...existingDiscountedSaleItem };
    discountedSaleItem.qty = quantityRoundOff(existingDiscountedSaleItem.qty + applyQty);
    if (rate !== null && rate !== undefined) {
      discountedSaleItem.rate = Number(rate);
    }
  }
  discountedSaleItem.maxQty = totalMaxQty;

  calculateSaleItemSummary(discountedSaleItem, { orderType, charges, selectedChargeGroupIds });
  return discountedSaleItem;
}

function applyNormalQtySaleItem(
  saleItems,
  existingProductSaleItems,
  product,
  remainingQty,
  note,
  rate,
  option,
  orderType,
  charges,
  selectedChargeGroupIds,
) {
  const existingNormalSaleItem = existingProductSaleItems.find((si) => si.maxQty === null);
  let normalSaleItem = existingNormalSaleItem ?? null;

  if (!normalSaleItem) {
    normalSaleItem = createNewStandardSaleItem(product, remainingQty, note, rate, option, null);
  } else {
    normalSaleItem.qty = quantityRoundOff(normalSaleItem.qty + remainingQty);
  }

  calculateSaleItemSummary(normalSaleItem, { orderType, charges, selectedChargeGroupIds });
  saleItems.push(normalSaleItem);
}

function getAddonDiscountContext(eligibleAddonSales, existingProductSaleItems, rate, qty) {
  const totalMaxQty = eligibleAddonSales.reduce((sum, sale) => sum + sale.maxQty, 0);

  const bestDelta = Math.min(...eligibleAddonSales.map((sale) => sale.priceDelta));

  const existingDiscountedSaleItem = existingProductSaleItems.find((item) => item.maxQty !== null) ?? null;

  const existingDiscountedQty = existingDiscountedSaleItem?.qty ?? 0;

  return {
    discountedRate: rate + bestDelta,
    totalMaxQty,
    remainingQty: qty,
    existingDiscountedSaleItem,
    remainingDiscountQty: totalMaxQty - existingDiscountedQty,
  };
}

function applyDiscountedQty({
  saleItems,
  product,
  note,
  option,
  rate,
  discountedRate,
  totalMaxQty,
  remainingQty,
  remainingDiscountQty,
  existingDiscountedSaleItem,
  orderType,
  charges,
  selectedChargeGroupIds,
}) {
  if (remainingDiscountQty <= 0) {
    return remainingQty;
  }

  const applyQty = Math.min(remainingQty, remainingDiscountQty);

  saleItems.push(
    applyAddonDiscountedQtyItem(
      existingDiscountedSaleItem,
      product,
      applyQty,
      note,
      discountedRate,
      option,
      totalMaxQty,
      rate,
      orderType,
      charges,
      selectedChargeGroupIds,
    ),
  );

  return quantityRoundOff(remainingQty - applyQty);
}
export const processAddonDiscountedSaleItems = ({
  saleItems,
  product,
  qty,
  note,
  rate,
  option,
  eligibleAddonSales,
  orderType,
  charges = [],
  selectedChargeGroupIds = null,
}) => {
  const existingProductSaleItems = getExistingSaleItems(saleItems, product, option);

  const { discountedRate, totalMaxQty, existingDiscountedSaleItem, remainingDiscountQty, remainingQty } =
    getAddonDiscountContext(eligibleAddonSales, existingProductSaleItems, rate, qty);

  const balanceQty = applyDiscountedQty({
    saleItems,
    product,
    note,
    option,
    rate,
    discountedRate,
    totalMaxQty,
    remainingQty,
    remainingDiscountQty,
    existingDiscountedSaleItem,
    orderType,
    charges,
    selectedChargeGroupIds,
  });

  if (balanceQty > 0) {
    applyNormalQtySaleItem(
      saleItems,
      existingProductSaleItems,
      product,
      balanceQty,
      note,
      rate,
      option,
      orderType,
      charges,
      selectedChargeGroupIds,
    );
  }
};

/**
 * Calculates the final price for a sale item considering the selected price category mapping.
 * Falls back to the standard product price if no specific category match is found.
 * @param {Object} product - The product object being priced.
 * @param {number} quantity - The quantity of the product being added or priced.
 * @param {Object|null} priceCategory - The currently selected price category object.
 * @param {Array} priceCategoryItems - The full list of mapped category pricing from the store.
 * @returns {number} The evaluated correct price for this product.
 */
export const getSaleItemRate = (product, quantity, priceCategory, priceCategoryItems, option) => {
  if (priceCategory) {
    const categoryPrice = CartHelper.getApplicableCategoryPrice(
      product.id,
      quantity,
      priceCategory,
      priceCategoryItems,
    );
    if (categoryPrice !== null) return categoryPrice;
  }
  return option?.price ?? product?.price;
};

export const extractOrderTypeChargeGroupIds = (orderTypeConfig) => {
  if (Array.isArray(orderTypeConfig)) {
    return orderTypeConfig.map(Number);
  }
  if (Array.isArray(orderTypeConfig?.charge_groups)) {
    return orderTypeConfig.charge_groups.map(Number);
  }
  return [];
};

export const getApplicableChargeGroupIds = (orderType, selectedChargeGroups = []) => {
  const shopProfile = ShopsHelper.getCachedShopProfile();
  const shopChargeGroupId = shopProfile?.charge_group;
  const orderTypesConfig = shopProfile?.config?.order_types || {};
  const orderTypeGroupIds = orderType ? extractOrderTypeChargeGroupIds(orderTypesConfig[orderType]) : [];

  const manualGroupIds = (selectedChargeGroups || []).map((chargeGroup) => chargeGroup?.id);

  const allIds = [];
  if (shopChargeGroupId) allIds.push(Number(shopChargeGroupId));
  orderTypeGroupIds.forEach((id) => allIds.push(Number(id)));
  manualGroupIds.forEach((id) => allIds.push(Number(id)));

  return [...new Set(allIds)];
};

export const getActiveChargeGroupIds = (sale) => {
  const shopProfile = ShopsHelper.getCachedShopProfile();
  const shopChargeGroupId = shopProfile?.charge_group ? Number(shopProfile.charge_group) : null;
  const orderTypesConfig = shopProfile?.config?.order_types || {};
  const orderTypeGroupIds = sale?.order_type ? extractOrderTypeChargeGroupIds(orderTypesConfig[sale.order_type]) : [];

  const itemGroupIds = [];
  (sale?.items || []).forEach((si) => {
    const itemChargeGroups = Array.isArray(si.item?.charge_groups) ? si.item.charge_groups : [];
    itemChargeGroups.forEach((id) => itemGroupIds.push(Number(id)));
  });

  const defaultIds = [...(shopChargeGroupId ? [shopChargeGroupId] : []), ...orderTypeGroupIds, ...itemGroupIds];
  const uniqueDefaultIds = [...new Set(defaultIds)];

  let selectedChargeGroupIds = uniqueDefaultIds;
  if (sale?.chargeGroupsCustomized) {
    selectedChargeGroupIds = (sale.selectedChargeGroups || []).map((g) => Number(g.id));
  }

  const defaultSaleLevelIds = [...(shopChargeGroupId ? [shopChargeGroupId] : []), ...orderTypeGroupIds];

  const activeSaleLevelIds = [
    ...defaultSaleLevelIds,
    ...selectedChargeGroupIds.filter((id) => !itemGroupIds.includes(Number(id))),
  ].filter((id) => selectedChargeGroupIds.includes(Number(id)));

  const uniqueActiveSaleLevelIds = [...new Set(activeSaleLevelIds)];

  return {
    selectedChargeGroupIds,
    saleLevelChargeGroupIds: uniqueActiveSaleLevelIds,
  };
};

export const recalculateSaleItems = async (sale, selectedChargeGroupIds, charges = []) => {
  return await Promise.all(
    (sale.items || []).map(async (saleItem) => {
      const product = await ProductHelper.getProductById(saleItem.id);
      if (!product) return saleItem;

      const existingRate = TaxHelper.getSaleItemBaseRate(saleItem);
      const rateToPass = existingRate ?? saleItem.option?.price ?? product.price;

      const data = CartHelper.getCartItemData(
        product,
        saleItem,
        saleItem.qty,
        {
          orderType: sale.order_type,
          rate: rateToPass,
          selectedChargeGroupIds,
        },
        charges,
      );
      return {
        ...data,
        localId: saleItem.localId || nanoid(),
      };
    }),
  );
};

export const getMappedOnlineStatus = (status) => {
  if (status === KOT_STATUS.PREPARING) return ONLINE_ORDER_STATUS.PREPARING;
  if (status === KOT_STATUS.READY) return ONLINE_ORDER_STATUS.READY;
  if (status === KOT_STATUS.DISPATCH) return ONLINE_ORDER_STATUS.DELIVERED;
  return ONLINE_ORDER_STATUS.PENDING;
};

export const updateSaleKotStatus = async (saleId, newOrderStatus, options = {}) => {
  const sale = await getSaleById(saleId);
  if (!sale) return null;

  let updatedItems = options.itemUpdates || sale.items;

  if (newOrderStatus === KOT_STATUS.READY || newOrderStatus === KOT_STATUS.DISPATCH) {
    updatedItems = (updatedItems || []).map((item) => ({ ...item, status: KOT_STATUS.READY }));
  } else if (newOrderStatus === KOT_STATUS.PREPARING) {
    updatedItems = (updatedItems || []).map((item) => ({
      ...item,
      status: item.status === KOT_STATUS.READY ? KOT_STATUS.READY : KOT_STATUS.PREPARING,
    }));
  } else if (newOrderStatus === KOT_STATUS.PENDING) {
    updatedItems = (updatedItems || []).map((item) => ({
      ...item,
      status: item.status === KOT_STATUS.DRAFT ? KOT_STATUS.PENDING : item.status,
    }));
  }

  const updatedSale = { ...sale, kot_status: newOrderStatus, items: updatedItems };
  await saveSaleInDB(updatedSale, options);

  return updatedSale;
};

function buildOnlineOrderPaymentMode(selectedShop, onlinePayment, fallbackAmount = 0) {
  const paymentStatus = (onlinePayment?.status || "").toUpperCase();
  const isPaid =
    paymentStatus === ONLINE_PAYMENT_STATUS.SUCCESS ||
    paymentStatus === ONLINE_PAYMENT_STATUS.PAID ||
    paymentStatus === ONLINE_PAYMENT_STATUS.COMPLETED;

  const paidAmount = isPaid ? Number(onlinePayment?.amount ?? fallbackAmount) || 0 : 0;
  const onlinePaymentModeId = selectedShop?.online_order_payment_mode;
  const paymentModesList = ShopsHelper.getPaymentModes() || [];
  const onlinePaymentMode = paymentModesList.find((m) => m.id === onlinePaymentModeId) ||
    paymentModesList.find((m) => m.name?.toLowerCase().includes("online")) ||
    paymentModesList[0] || { id: 1, name: "cash" };

  const paymentMode = {
    id: onlinePaymentMode.id,
    name: onlinePaymentMode.name,
    amount: paidAmount,
  };

  if (onlinePayment) {
    paymentMode.tx_id = onlinePayment.tx_id || onlinePayment.transactionId;
    paymentMode.payment_id = onlinePayment.id;
    paymentMode.status = onlinePayment.status;
    paymentMode.currency = onlinePayment.currency;
    paymentMode.remarks = onlinePayment.remarks;
    paymentMode.action = onlinePayment.action;
    paymentMode.initiated_at = onlinePayment.initiated_at || onlinePayment.initiatedAt;
    paymentMode.updated_at = onlinePayment.updated_at || onlinePayment.updatedAt;
  }

  return [paymentMode];
}

export async function buildOnlineOrderSaleObject(order, items, charges, selectedShop) {
  const unavailableItems = [];
  const orderItems = order.details?.items || [];
  for (const orderItem of orderItems) {
    const posItem = items.find((it) => it.id === orderItem.id);
    if (!posItem) {
      unavailableItems.push(orderItem.name);
    }
  }

  if (unavailableItems.length > 0) {
    throw new Error(`Below items not available in the POS system: ${unavailableItems.join(", ")}`);
  }

  const saleItems = orderItems.map((orderItem) => {
    const product = items.find((it) => it.id === orderItem.id);
    const option = orderItem.option
      ? product?.config?.product_options?.find((opt) => opt.label === orderItem.option)
      : null;
    return CartHelper.getCartItemData(
      product,
      {
        localId: nanoid(),
        qty: orderItem.quantity,
        option,
        kotPrintedQty: 0,
        billLevelDiscount: 0,
        rate: orderItem.rate,
      },
      orderItem.quantity,
      { orderType: ORDER_TYPES.ONLINE, rate: orderItem.rate },
      charges,
    );
  });

  const totals = calculateSaleSummary(saleItems, { charges, orderType: ORDER_TYPES.ONLINE });
  const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);

  const tableName = order.details?.customer?.table;
  let tableData = null;

  if (tableName) {
    try {
      const allTables = (await TableAreaHelper.getTablesFromDB()) || [];
      const matched = allTables.find(
        (t) =>
          String(t.name || "")
            .trim()
            .toLowerCase() === String(tableName).trim().toLowerCase(),
      );
      tableData = matched || { name: tableName };
    } catch (e) {
      console.error("Failed to match table from DB for online order:", e);
      tableData = { name: tableName };
    }
  }

  return {
    items: saleItems,
    payment_modes: buildOnlineOrderPaymentMode(selectedShop, order.online_payment, totals.payable),
    customer: order.customer ? { ...order.customer, mobile: order.customer.mobile || "" } : undefined,
    table: tableData,
    remark: `${order.source || "Online"} order #${order.id}. ${order.description || ""}`,
    order_type: ORDER_TYPES.ONLINE,
    status: SALE_STATUS.DRAFT,
    kot_status: KOT_STATUS.PREPARING,
    online_order_id: order.id,
    online_order_source: order.source,
    shift: { id: Number(shiftId) },
    time: DateTimeHelpers.getLocalISOString(),
    deletedItems: [],
    selectedChargeGroups: [],
    ...totals,
  };
}

export function validateSaleFinalization(activeSale, due, paymentModes, activeCustomerDetails) {
  if (!activeSale || !activeSale.items || activeSale.items.length === 0) {
    toast.error("No sale/items to process.");
    return { valid: false };
  }

  const isNC = activeSale?.order_type === ORDER_TYPES.NC;
  const hasCustomer = Boolean(activeCustomerDetails?.id);

  if (isNC && !hasCustomer) {
    toast.error("Please select a customer before creating an NC invoice.");
    return { valid: false };
  }

  const terminalSettings = TerminalHelper.getTerminalSettings();

  if (due > 0 && !!terminalSettings.orderFullPayment) {
    toast.error("Tender is less than the bill total.");
    return { valid: false };
  }

  // TODO: Check with payment mode id
  const creditPayment = paymentModes.find((m) => m.name === PAYMENT_MODES.CREDIT);
  let creditAmount = creditPayment?.amount || 0;
  let initialCreditAmount = creditPayment?.initialAmount || 0;
  let additionalCreditRequested = Math.max(0, creditAmount - initialCreditAmount);

  if (additionalCreditRequested > 0) {
    const { eligible, reason } = CustomerHelper.isCustomerEligibleForCredit(
      activeCustomerDetails,
      additionalCreditRequested,
    );
    if (!eligible) {
      toast.error(reason);
      return { valid: false };
    }
  }

  return { valid: true };
}

export function computeKeypadString(val, keypadString) {
  val = String(val);
  let amountStr = (keypadString ?? "").toString();
  if (val === KEYPAD_BUTTONS.CLEAR || val === KEYPAD_BUTTONS.BACKSPACE) {
    amountStr = "";
  } else if (val === KEYPAD_BUTTONS.DELETE) {
    amountStr = amountStr.slice(0, -1) || "";
    if (amountStr === "-") amountStr = "";
  } else if (val === KEYPAD_BUTTONS.DOT || val === ".") {
    if (amountStr === "-") {
      amountStr = "-0.";
    } else if (!amountStr.includes(".")) {
      amountStr = amountStr === "" ? "0." : amountStr + ".";
    }
  } else if (val === KEYPAD_BUTTONS.PLUS_MINUS) {
    if (amountStr.startsWith("-")) {
      amountStr = amountStr.slice(1);
    } else {
      amountStr = amountStr === "" || amountStr === "0" ? "-" : "-" + amountStr;
    }
  } else if (val.startsWith("+")) {
    const increment = Number(val.replace("+", "")) || 0;
    const newNum = (parseFloat(amountStr) || 0) + increment;
    amountStr = newNum.toString();
  } else if (!isNaN(val) && val.length === 1) {
    if (amountStr === "-") {
      amountStr = `-${val}`;
    } else if (amountStr === "0") {
      amountStr = val;
    } else {
      amountStr += val;
    }
  }
  return amountStr;
}

export function validateAndClampPaymentModeAmount(
  activeMode,
  finalAmount,
  amountStr,
  activeCustomerDetails,
  paymentModes,
  selectedShop,
  activeSale,
) {
  const activeModeObj = paymentModes.find((m) => m.name === activeMode);
  const initialAmount = activeModeObj?.initialAmount || 0;
  let clampedAmount = finalAmount;
  let clampedStr = amountStr;

  if (activeMode === PAYMENT_MODES.CREDIT && activeCustomerDetails) {
    const maxAllowed = CustomerHelper.getAvailableCredit(activeCustomerDetails) + initialAmount;
    if (clampedAmount > maxAllowed) {
      toast.error(`Amount exceeds available credit limit (${maxAllowed})`);
      clampedAmount = maxAllowed;
      clampedStr = maxAllowed.toString();
    }
  }

  if (activeMode === PAYMENT_MODES.POINTS && activeCustomerDetails) {
    const points = Number(activeCustomerDetails.loyalty_points || activeCustomerDetails.points || 0);
    const conversionRate = Number(selectedShop?.loyalty_point_conversion_rate || 1);
    const maxAllowedCurrency = points * conversionRate + initialAmount;

    if (clampedAmount > maxAllowedCurrency) {
      toast.error(`Amount exceeds available points value (${maxAllowedCurrency})`);
      clampedAmount = maxAllowedCurrency;
      clampedStr = maxAllowedCurrency.toString();
    }
  }

  if (activeMode === PAYMENT_MODES.DISCOUNT) {
    const currentTender = paymentModes
      .filter((m) => m.name !== PAYMENT_MODES.DISCOUNT)
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    const remainingDue = amountRoundOff(Math.max((activeSale?.payable || 0) - currentTender, 0));

    if (remainingDue <= 0 && clampedAmount > 0) {
      toast.error("Bill is already fully paid. Cannot apply discount.");
      return { valid: false };
    }

    if (clampedAmount > remainingDue) {
      toast.error(`Discount cannot exceed remaining due (${NumberFormatHelper.roundOff(remainingDue)})`);
      clampedAmount = remainingDue;
      clampedStr = remainingDue.toString();
    }
  }

  if (activeMode === PAYMENT_MODES.DISCOUNT_PERCENTAGE) {
    const currentTender = paymentModes
      .filter((m) => m.name !== PAYMENT_MODES.DISCOUNT && m.name !== PAYMENT_MODES.DISCOUNT_PERCENTAGE)
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    const remainingDue = amountRoundOff(Math.max((activeSale?.payable || 0) - currentTender, 0));

    if (remainingDue <= 0 && clampedAmount > 0) {
      toast.error("Bill is already fully paid. Cannot apply discount.");
      return { valid: false };
    }

    if (clampedAmount > 100) {
      toast.error("Discount percentage cannot exceed 100%");
      clampedAmount = 100;
      clampedStr = "100";
    }

    if (clampedAmount < 0) {
      clampedAmount = 0;
      clampedStr = "0";
    }
  }

  return { valid: true, finalAmount: clampedAmount, amountStr: clampedStr };
}

export function handleFunctionKeysNavigation(event, handlers = {}) {
  const {
    onDefaultActionBtnClick,
    onSaveInvoiceClick,
    onKotAndPrintClick,
    onSaveAndWhatsAppClick,
    onDirectPrintClick,
  } = handlers;
  if (event.key === KEYBOARD_KEYS.F4) {
    event.preventDefault();
    onDefaultActionBtnClick();
    return true;
  }
  if (event.key === KEYBOARD_KEYS.F5) {
    event.preventDefault();
    onSaveInvoiceClick();
    return true;
  }
  if (event.key === KEYBOARD_KEYS.F6) {
    event.preventDefault();
    onKotAndPrintClick();
    return true;
  }
  if (event.key === KEYBOARD_KEYS.F7) {
    event.preventDefault();
    onSaveAndWhatsAppClick();
    return true;
  }
  if (event.key === KEYBOARD_KEYS.F8) {
    event.preventDefault();
    onDirectPrintClick();
    return true;
  }
  return false;
}

export function handleArrowKeysNavigation(event, sortedPaymentModes, activeMode, handlePaymentModeSelect) {
  if (event.key === KEYBOARD_KEYS.ARROW_RIGHT) {
    event.preventDefault();
    if (sortedPaymentModes.length > 0) {
      const currentIndex = sortedPaymentModes.findIndex((m) => m.name === activeMode);
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % sortedPaymentModes.length;
      handlePaymentModeSelect(sortedPaymentModes[nextIndex]);
    }
    return true;
  }
  if (event.key === KEYBOARD_KEYS.ARROW_LEFT) {
    event.preventDefault();
    if (sortedPaymentModes.length > 0) {
      const currentIndex = sortedPaymentModes.findIndex((m) => m.name === activeMode);
      const prevIndex =
        currentIndex === -1
          ? sortedPaymentModes.length - 1
          : (currentIndex - 1 + sortedPaymentModes.length) % sortedPaymentModes.length;
      handlePaymentModeSelect(sortedPaymentModes[prevIndex]);
    }
    return true;
  }
  return false;
}

export function handleKeypadKeysInput(event, activeMode, handleKeypadInput) {
  if (!activeMode) return false;
  if (event.key >= "0" && event.key <= "9") {
    event.preventDefault();
    handleKeypadInput(event.key);
    return true;
  }
  if (event.key === KEYBOARD_KEYS.DOT) {
    event.preventDefault();
    handleKeypadInput(KEYPAD_BUTTONS.DOT);
    return true;
  }
  if (event.key === KEYBOARD_KEYS.BACKSPACE) {
    event.preventDefault();
    handleKeypadInput(KEYPAD_BUTTONS.DELETE);
    return true;
  }
  if (event.key === KEYBOARD_KEYS.C_LOWER || event.key === KEYBOARD_KEYS.C_UPPER) {
    event.preventDefault();
    handleKeypadInput(KEYPAD_BUTTONS.CLEAR);
    return true;
  }
  if (event.key === KEYBOARD_KEYS.MINUS) {
    event.preventDefault();
    handleKeypadInput(KEYPAD_BUTTONS.PLUS_MINUS);
    return true;
  }
  return false;
}

function resolveInitialItemRate(
  rate,
  product,
  totalProductInCartQuantity,
  quantity,
  selectedPriceCategory,
  priceCategoryItems,
  option,
) {
  return (
    rate ??
    getSaleItemRate(product, totalProductInCartQuantity + quantity, selectedPriceCategory, priceCategoryItems, option)
  );
}

function processItemIntoSaleItems(
  saleItems,
  product,
  quantity,
  note,
  option,
  initialRate,
  activeSale,
  addonMappings,
  charges,
  selectedChargeGroupIds,
  selectedPriceCategory,
  priceCategoryItems,
) {
  const eligibleAddonSales = getEligibleAddonSales(saleItems, addonMappings, product);
  if (eligibleAddonSales.length === 0) {
    processStandardSaleItem({
      saleItems,
      product,
      qty: quantity,
      note,
      rate: initialRate,
      option,
      orderType: activeSale.order_type,
      charges,
      selectedChargeGroupIds,
      priceCategory: selectedPriceCategory,
      priceCategoryItems,
    });
  } else {
    processAddonDiscountedSaleItems({
      saleItems,
      product,
      qty: quantity,
      note,
      rate: initialRate,
      option,
      eligibleAddonSales,
      orderType: activeSale.order_type,
      charges,
      selectedChargeGroupIds,
    });
  }
}

function getProductSaleContext(activeSale, product, option) {
  const saleItems = [...(activeSale.items || [])];

  const existingProductSaleItems = getExistingSaleItems(saleItems, product, option);

  const totalProductInCartQuantity = existingProductSaleItems.reduce((sum, item) => sum + item.qty, 0);

  return {
    saleItems,
    totalProductInCartQuantity,
  };
}

function validateProductStock(product, quantity, totalProductInCartQuantity) {
  if (ConfigHelper.preventNegativeStockBilling() !== STOCK_CHECK_OPTIONS.TERMINAL.value) {
    return true;
  }

  if (CartHelper.isStockAvailableForSale(product, totalProductInCartQuantity + quantity)) {
    return true;
  }

  toast.error("Stock not available to add");
  return false;
}

function buildUpdatedSaleObject(activeSale, saleItems, product, charges) {
  const { selectedChargeGroupIds, saleLevelChargeGroupIds } = getActiveChargeGroupIds({
    ...activeSale,
    items: [...saleItems, { item: product }],
  });

  const totals = calculateSaleSummary(saleItems, {
    charges,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    orderType: activeSale.order_type,
  });

  const dueAndChange = calculateDueAndChange(activeSale, totals.payable);

  return {
    ...activeSale,
    items: saleItems,
    saleLevelChargeGroupIds,
    selectedChargeGroupIds,
    ...totals,
    ...dueAndChange,
  };
}
export function handleAddProductItemsToSale(
  activeSale,
  product,
  quantity,
  note,
  option,
  rate,
  selectedPriceCategory,
  priceCategoryItems,
  addonMappings,
  charges,
) {
  const { saleItems, totalProductInCartQuantity } = getProductSaleContext(activeSale, product, option);

  if (!validateProductStock(product, quantity, totalProductInCartQuantity)) {
    return null;
  }

  const initialRate = resolveInitialItemRate(
    rate,
    product,
    totalProductInCartQuantity,
    quantity,
    selectedPriceCategory,
    priceCategoryItems,
    option,
  );

  const { selectedChargeGroupIds } = getActiveChargeGroupIds({
    ...activeSale,
    items: [...saleItems, { item: product }],
  });

  processItemIntoSaleItems(
    saleItems,
    product,
    quantity,
    note,
    option,
    initialRate,
    activeSale,
    addonMappings,
    charges,
    selectedChargeGroupIds,
    selectedPriceCategory,
    priceCategoryItems,
  );

  return buildUpdatedSaleObject(activeSale, saleItems, product, charges);
}

export function buildOrUpdateSaleItem(existingItem, itemRequest, initialRate) {
  const {
    item,
    qty = 1,
    note,
    option = null,
    discountPercentage: overrideDiscount = null,
    taxPercentage: overrideTax = null,
    discountAmount: overrideDiscountAmount = null,
    manualRate = null,
  } = itemRequest;

  if (existingItem) {
    const saleItem = { ...existingItem };
    saleItem.qty = quantityRoundOff(saleItem.qty + qty);
    saleItem.optionQty = quantityRoundOff(saleItem.optionQty + (option?.quantityRatio ?? 1) * qty);
    saleItem.note = note || saleItem.note;
    if (manualRate != null) {
      saleItem.manualRate = Number(manualRate);
    }
    return saleItem;
  }

  const initialDiscountPercentage =
    overrideDiscount !== null
      ? Number(overrideDiscount)
      : overrideDiscountAmount !== null && initialRate > 0
        ? (Number(overrideDiscountAmount) / (initialRate * qty)) * 100
        : 0;

  return {
    localId: nanoid(),
    item,
    name: item.name,
    id: item.id,
    productId: item.id,
    qty: quantityRoundOff(qty),
    rate: initialRate,
    manualRate: manualRate != null ? Number(manualRate) : null,
    optionQty: quantityRoundOff((option?.quantityRatio ?? 1) * qty),
    kotPrintedQty: 0,
    discount: overrideDiscountAmount !== null ? Number(overrideDiscountAmount) : 0,
    discountPercentage: initialDiscountPercentage,
    billLevelDiscount: 0,
    taxPercentage: overrideTax !== null ? Number(overrideTax) : item.tax,
    cessPercentage: item.cess,
    note,
    option,
    maxQty: item?.maxQty ?? null,
  };
}

export function handleSaleItemDeletedUpdates(updatedSaleItem, additionalUpdates, activeSale) {
  let mergedAdditionalUpdates = { ...additionalUpdates };
  if (updatedSaleItem.kotPrintedQty > 0) {
    let currentDeletedItems = mergedAdditionalUpdates.deletedItems ?? activeSale.deletedItems ?? [];
    if (updatedSaleItem.qty >= updatedSaleItem.kotPrintedQty) {
      mergedAdditionalUpdates.deletedItems = currentDeletedItems.filter((it) => it.localId !== updatedSaleItem.localId);
    } else {
      const filtered = currentDeletedItems.filter((it) => it.localId !== updatedSaleItem.localId);
      const newQty = Math.max(0, updatedSaleItem.qty || 0);
      mergedAdditionalUpdates.deletedItems = [
        ...filtered,
        {
          ...updatedSaleItem,
          newQty,
          qty: updatedSaleItem.kotPrintedQty - newQty,
        },
      ];
    }
  }
  return mergedAdditionalUpdates;
}

export function combineDeduplicatedSaleItems(salesToMerge) {
  const itemsMap = new Map();
  salesToMerge.forEach((s) => {
    if (s.items) {
      s.items.forEach((item) => {
        const key = `${item.productId || item.id}_${item.option?.label || "default"}`;
        if (itemsMap.has(key)) {
          const existing = itemsMap.get(key);
          existing.qty = (existing.qty || 0) + (item.qty || 1);
          existing.kotPrintedQty = (existing.kotPrintedQty || 0) + (item.kotPrintedQty || 0);
          existing.payable = (existing.payable || 0) + (item.payable || 0);
          existing.tax = (existing.tax || 0) + (item.tax || 0);
          existing.cess = (existing.cess || 0) + (item.cess || 0);
          existing.discountAmount = (existing.discountAmount || 0) + (item.discountAmount || 0);

          if (item.note && item.note !== "-" && existing.note !== item.note) {
            existing.note = existing.note && existing.note !== "-" ? `${existing.note}, ${item.note}` : item.note;
          }
        } else {
          itemsMap.set(key, { ...item });
        }
      });
    }
  });

  return Array.from(itemsMap.values());
}

export const getInitialRate = (saleItem, isExcluding) =>
  String(
    (isExcluding ? saleItem?.rate : saleItem?.rateAndTax) ??
      saleItem?.rateAndTax ??
      saleItem?.item?.price ??
      saleItem?.rate ??
      0,
  );
